import { createContext, useRef, useState, useEffect, useLayoutEffect, useCallback, useContext, useMemo, useSyncExternalStore, useReducer, useId } from 'react';
import { Transport, AuthClient, PowerPortalsProClient, RecordPagesCache, LoginResult, SwitchIdentityResult, ImpersonateResult, StopImpersonationResult, writeBrowserTimeZoneCookie, PortalUserType, PagingCookies, keepingGroupability, operationRunsAlone, findRecordPage, canWriteColumn, TableSecurityPermission, getRecordReferenceId, canReadColumn, hasColumn, resolveColumnDisplayValue, readColumnRawValue } from '@powerportalspro/core';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/masking/reformat.ts
var MaskMode = {
  /** No masking — value passes through unchanged. */
  None: "none",
  /**
   * Per-character allow filter. Characters that don't match
   * {@link MaskOptions.allowedPattern} are dropped as they're typed or pasted.
   */
  Regex: "regex",
  /**
   * Slotted template (see {@link MaskOptions.pattern}) such as `(000) 000-0000`.
   * `0` = digit, `A` = letter, `*` = alphanumeric; every other character is a
   * literal inserted automatically.
   */
  Pattern: "pattern",
  /**
   * Live numeric grouping driven by {@link MaskOptions.numeric} — thousands
   * separators, an optional decimal portion, and an optional leading sign.
   */
  Numeric: "numeric"
};
var DIGIT = /[0-9]/;
var LETTER = /[a-zA-Z]/;
var ALNUM = /[a-zA-Z0-9]/;
function isPatternLiteral(token) {
  return token !== "0" && token !== "A" && token !== "*";
}
function patternTokenMatches(token, ch) {
  if (token === "0") return DIGIT.test(ch);
  if (token === "A") return LETTER.test(ch);
  if (token === "*") return ALNUM.test(ch);
  return false;
}
function countSignificant(source, caret, isSignificant) {
  let n = 0;
  for (let i = 0; i < caret && i < source.length; i++) {
    if (isSignificant(source.charAt(i))) n++;
  }
  return n;
}
function caretAfterSignificant(display, target, isSignificant) {
  if (target <= 0) {
    let i = 0;
    while (i < display.length && !isSignificant(display.charAt(i))) i++;
    return i;
  }
  let seen = 0;
  for (let i = 0; i < display.length; i++) {
    if (isSignificant(display.charAt(i))) {
      seen++;
      if (seen === target) return i + 1;
    }
  }
  return display.length;
}
function reformatNumeric(value, caret, o) {
  const sigChars = (c) => DIGIT.test(c) || c === o.decimalSeparator || o.allowNegative && c === "-";
  const sigBefore = countSignificant(value, caret, sigChars);
  const negative = o.allowNegative && value.trimStart().startsWith("-");
  let intPart = "";
  let fracPart = "";
  let sawDecimal = false;
  for (const ch of value) {
    if (DIGIT.test(ch)) {
      if (sawDecimal) fracPart += ch;
      else intPart += ch;
    } else if (o.allowDecimal && ch === o.decimalSeparator && !sawDecimal) {
      sawDecimal = true;
    }
  }
  intPart = intPart.replace(/^0+(?=\d)/, "");
  if (o.maxDecimals !== null && fracPart.length > o.maxDecimals) {
    fracPart = fracPart.slice(0, o.maxDecimals);
  }
  const grouped = o.groupSeparator ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, o.groupSeparator) : intPart;
  const sign = negative ? "-" : "";
  let display = sign + grouped;
  let raw = sign + intPart;
  if (sawDecimal) {
    display += o.decimalSeparator + fracPart;
    raw += o.decimalSeparator + fracPart;
  }
  const newCaret = caretAfterSignificant(display, sigBefore, sigChars);
  return { display, raw, caret: newCaret };
}
function reformatPattern(value, caret, pattern) {
  const sourceChars = [];
  for (const ch of value) {
    if (DIGIT.test(ch) || LETTER.test(ch)) sourceChars.push(ch);
  }
  const sigBefore = countSignificant(value, caret, (c) => DIGIT.test(c) || LETTER.test(c));
  let display = "";
  let raw = "";
  let si = 0;
  for (let pi = 0; pi < pattern.length && si <= sourceChars.length; pi++) {
    const token = pattern[pi];
    if (isPatternLiteral(token)) {
      if (si < sourceChars.length) display += token;
      continue;
    }
    while (si < sourceChars.length && !patternTokenMatches(token, sourceChars[si])) si++;
    if (si < sourceChars.length) {
      display += sourceChars[si];
      raw += sourceChars[si];
      si++;
    } else {
      break;
    }
  }
  const newCaret = caretAfterSignificant(display, sigBefore, (c) => DIGIT.test(c) || LETTER.test(c));
  return { display, raw, caret: newCaret };
}
function reformatRegex(value, caret, allowedPattern) {
  let re;
  try {
    re = new RegExp(allowedPattern);
  } catch {
    return { display: value, raw: value, caret };
  }
  const keep = (c) => re.test(c);
  const sigBefore = countSignificant(value, caret, keep);
  let display = "";
  for (const ch of value) {
    if (keep(ch)) display += ch;
  }
  const newCaret = caretAfterSignificant(display, sigBefore, keep);
  return { display, raw: display, caret: newCaret };
}
function reformat(value, caret, options) {
  switch (options.mode) {
    case MaskMode.Numeric:
      return options.numeric ? reformatNumeric(value, caret, options.numeric) : { display: value, raw: value, caret };
    case MaskMode.Pattern:
      return options.pattern ? reformatPattern(value, caret, options.pattern) : { display: value, raw: value, caret };
    case MaskMode.Regex:
      return options.allowedPattern ? reformatRegex(value, caret, options.allowedPattern) : { display: value, raw: value, caret };
    default:
      return { display: value, raw: value, caret };
  }
}
function computeDisplay(value, options) {
  if (value === null || value === void 0 || value === "") return "";
  return reformat(value, value.length, options).display;
}
function computeReported(value, options) {
  if (value === null || value === void 0 || value === "") return "";
  const r = reformat(value, value.length, options);
  return options.valueIncludesMask ? r.display : r.raw;
}
function useMaskedTextField(opts) {
  const {
    value,
    onValueChange,
    mode = MaskMode.None,
    pattern = null,
    allowedPattern = null,
    numeric = null,
    valueIncludesMask = false
  } = opts;
  const optionsRef = useRef({
    mode,
    pattern,
    allowedPattern,
    numeric,
    valueIncludesMask
  });
  optionsRef.current = { mode, pattern, allowedPattern, numeric, valueIncludesMask };
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;
  const elementRef = useRef(null);
  const focusedRef = useRef(false);
  const hasInputSinceFocusRef = useRef(false);
  const lastReportedRef = useRef(null);
  const pendingCaretRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(
    () => computeDisplay(value, optionsRef.current)
  );
  useEffect(() => {
    if (focusedRef.current && hasInputSinceFocusRef.current) return;
    const next = computeDisplay(value, optionsRef.current);
    setDisplayValue(next);
    lastReportedRef.current = computeReported(value, optionsRef.current);
  }, [value]);
  useEffect(() => {
    if (focusedRef.current && hasInputSinceFocusRef.current) return;
    const next = computeDisplay(value, optionsRef.current);
    setDisplayValue(next);
    lastReportedRef.current = computeReported(value, optionsRef.current);
  }, [mode, pattern, allowedPattern, numeric, valueIncludesMask]);
  useLayoutEffect(() => {
    const target = pendingCaretRef.current;
    if (target === null) return;
    const input = elementRef.current;
    if (!input) return;
    try {
      input.setSelectionRange(target, target);
    } catch {
    }
    pendingCaretRef.current = null;
  });
  const onChange = useCallback(
    (_event, data) => {
      hasInputSinceFocusRef.current = true;
      const o = optionsRef.current;
      const input = elementRef.current;
      const caret = input?.selectionStart ?? data.value.length;
      const result = reformat(data.value, caret, o);
      setDisplayValue(result.display);
      pendingCaretRef.current = result.caret;
      const reported = o.valueIncludesMask ? result.display : result.raw;
      const empty = reported === "";
      const next = empty ? null : reported;
      if (reported !== lastReportedRef.current) {
        lastReportedRef.current = reported;
        onValueChangeRef.current(next);
      }
    },
    []
  );
  const inputRef = useCallback((el) => {
    const prev = elementRef.current;
    if (prev && prev !== el) {
      prev.removeEventListener("focus", handleFocusRef.current);
      prev.removeEventListener("blur", handleBlurRef.current);
    }
    elementRef.current = el;
    if (el) {
      el.addEventListener("focus", handleFocusRef.current);
      el.addEventListener("blur", handleBlurRef.current);
    }
  }, []);
  const handleFocusRef = useRef(() => {
  });
  handleFocusRef.current = () => {
    focusedRef.current = true;
    hasInputSinceFocusRef.current = false;
  };
  const handleBlurRef = useRef(() => {
  });
  handleBlurRef.current = () => {
    focusedRef.current = false;
    if (!hasInputSinceFocusRef.current) {
      const next = computeDisplay(value, optionsRef.current);
      if (next !== displayValue) {
        setDisplayValue(next);
        lastReportedRef.current = computeReported(value, optionsRef.current);
      }
    }
  };
  const forceValue = useCallback((newValue) => {
    const o = optionsRef.current;
    if (newValue === null || newValue === "") {
      setDisplayValue("");
      lastReportedRef.current = "";
      pendingCaretRef.current = 0;
      onValueChangeRef.current(null);
      return;
    }
    const result = reformat(newValue, newValue.length, o);
    setDisplayValue(result.display);
    pendingCaretRef.current = result.display.length;
    const reported = o.valueIncludesMask ? result.display : result.raw;
    lastReportedRef.current = reported;
    onValueChangeRef.current(reported === "" ? null : reported);
  }, []);
  return { displayValue, onChange, inputRef, forceValue };
}
var AuthStatus = {
  Loading: "loading",
  Anonymous: "anonymous",
  Authenticated: "authenticated"
};
var PowerPortalsProContext = createContext(null);

// src/caches/in-memory-cache.ts
var InMemoryCache = class {
  constructor(fetcher) {
    this.fetcher = fetcher;
  }
  fetcher;
  resolved = /* @__PURE__ */ new Map();
  inFlight = /* @__PURE__ */ new Map();
  async getAsync(key) {
    const cached = this.resolved.get(key);
    if (cached !== void 0) return cached;
    const pending = this.inFlight.get(key);
    if (pending) return pending;
    const promise = (async () => {
      try {
        const result = await this.fetcher(key);
        if (result !== null) this.resolved.set(key, result);
        return result;
      } finally {
        this.inFlight.delete(key);
      }
    })();
    this.inFlight.set(key, promise);
    return promise;
  }
  invalidate(key) {
    this.resolved.delete(key);
  }
  clear() {
    this.resolved.clear();
  }
};
var InMemoryTableMetadataCache = class _InMemoryTableMetadataCache {
  ppp;
  cache;
  /**
   * The header list, under a single key. Reuses the same `InMemoryCache` as the
   * per-table entries so it inherits the in-flight-promise dedup — several
   * pickers mounting at once share one round-trip rather than each firing their
   * own.
   */
  headers;
  static HEADERS_KEY = "__all-table-headers";
  constructor(ppp) {
    this.ppp = ppp;
    this.cache = new InMemoryCache(
      (tableLogicalName) => ppp.retrieveTableMetadataAsync(tableLogicalName)
    );
    this.headers = new InMemoryCache(
      () => ppp.retrieveTableHeadersAsync()
    );
  }
  getAsync(tableLogicalName) {
    return this.cache.getAsync(tableLogicalName);
  }
  async getAllHeadersAsync() {
    return await this.headers.getAsync(_InMemoryTableMetadataCache.HEADERS_KEY) ?? [];
  }
  /**
   * Passed straight to the server rather than assembled from the entries above.
   *
   * Answering it from cache would mean holding each named table in full, which is
   * the retrieval the batch exists to avoid; and the answer isn't cached on the way
   * back, because a narrowed result is not a table and storing one under a table's
   * key would leave the next {@link getAsync} short of columns.
   */
  async getManyAsync(requests) {
    if (requests.length === 0) return [];
    return await this.ppp.retrieveTableMetadataBatchAsync(requests);
  }
  invalidate(tableLogicalName) {
    this.cache.invalidate(tableLogicalName);
  }
  clear() {
    this.cache.clear();
    this.headers.clear();
  }
};
var InMemoryViewMetadataCache = class {
  cache;
  constructor(ppp) {
    this.cache = new InMemoryCache(
      (viewId) => ppp.retrieveViewMetadataAsync(viewId)
    );
  }
  getAsync(viewId) {
    return this.cache.getAsync(viewId);
  }
  invalidate(viewId) {
    this.cache.invalidate(viewId);
  }
  clear() {
    this.cache.clear();
  }
};
var InMemoryOrganizationSettingsCache = class _InMemoryOrganizationSettingsCache {
  static KEY = "organization-settings";
  cache;
  constructor(ppp) {
    this.cache = new InMemoryCache(
      () => ppp.getOrganizationSettingsAsync()
    );
  }
  getAsync() {
    return this.cache.getAsync(_InMemoryOrganizationSettingsCache.KEY);
  }
  invalidate() {
    this.cache.invalidate(_InMemoryOrganizationSettingsCache.KEY);
  }
};
var InMemoryViewsForTableCache = class {
  cache;
  constructor(ppp) {
    this.cache = new InMemoryCache(async (tableLogicalName) => {
      const result = await ppp.retrieveViewsForTableAsync(tableLogicalName);
      return result ?? [];
    });
  }
  async getAsync(tableLogicalName) {
    const result = await this.cache.getAsync(tableLogicalName);
    return result ?? [];
  }
  invalidate(tableLogicalName) {
    this.cache.invalidate(tableLogicalName);
  }
  clear() {
    this.cache.clear();
  }
};
function usePowerPortalsPro() {
  const ctx = useContext(PowerPortalsProContext);
  if (!ctx) {
    throw new Error(
      "usePowerPortalsPro must be used inside <PowerPortalsProProvider>. Wrap your app at or near the root."
    );
  }
  return ctx;
}
var LocalizerContext = createContext(null);
LocalizerContext.displayName = "Localizer";
function useLocalizer() {
  const ctx = useContext(LocalizerContext);
  if (!ctx) {
    throw new Error(
      "useLocalizer must be used inside a <LocalizerProvider>. PowerPortalsProProvider auto-mounts a default localizer; ensure your tree is wrapped."
    );
  }
  return ctx;
}
function useT() {
  return useLocalizer().t;
}
function usePrefixedT(prefix) {
  const t = useT();
  return useCallback(
    (key, args) => t(`${prefix}.${key}`, args),
    [t, prefix]
  );
}
var LocalizationPrefetcherContext = createContext(null);
LocalizationPrefetcherContext.displayName = "LocalizationPrefetcher";
function useLocalizationPrefetcher() {
  return useContext(LocalizationPrefetcherContext);
}
var NOOP_UNSUBSCRIBE = () => void 0;
function useLocalization(prefixes) {
  const prefetcher = useLocalizationPrefetcher();
  const prefixesKey = useMemo(() => {
    if (prefixes.length === 0) return "";
    const unique = Array.from(new Set(prefixes));
    unique.sort();
    return unique.join("\n");
  }, [prefixes]);
  useEffect(() => {
    if (!prefetcher) return;
    if (prefixesKey === "") return;
    const tokens = prefixesKey.split("\n");
    prefetcher.prefetch(tokens);
    return prefetcher.subscribe(() => {
      prefetcher.prefetch(tokens);
    });
  }, [prefetcher, prefixesKey]);
}
function useLocalizationLoaded(prefixes) {
  const prefetcher = useLocalizationPrefetcher();
  const subscribe = useCallback(
    (listener) => prefetcher?.subscribe(listener) ?? NOOP_UNSUBSCRIBE,
    [prefetcher]
  );
  const getSnapshot = useCallback(
    () => prefetcher ? prefetcher.isLoaded(prefixes) : true,
    [prefetcher, prefixes]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshotAlwaysLoaded);
}
var getServerSnapshotAlwaysLoaded = () => true;
function LocalizerProvider({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(LocalizerContext.Provider, { value, children });
}
function flattenStrings(obj, prefix = "") {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") {
      out[key] = v;
    } else if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flattenStrings(v, key));
    }
  }
  return out;
}
function interpolate(template, args) {
  if (!args || args.length === 0) return template;
  return template.replace(/\{(\d+)(?::[^}]*)?\}/g, (match, indexStr) => {
    const idx = Number(indexStr);
    if (idx < 0 || idx >= args.length) return match;
    return String(args[idx]);
  });
}
var LOCALIZATION_KEY_MODE_QUERY_PARAM = "ppp-show-keys";
var keyModeSearch;
var keyModeResult = false;
function isKeyModeEnabled() {
  if (typeof window === "undefined") return false;
  const search = window.location.search;
  if (search !== keyModeSearch) {
    keyModeSearch = search;
    const value = new URLSearchParams(search).get(LOCALIZATION_KEY_MODE_QUERY_PARAM);
    keyModeResult = value !== null && value !== "0" && value.toLowerCase() !== "false";
  }
  return keyModeResult;
}
function createLocalizer(strings, options) {
  const warnedKeys = /* @__PURE__ */ new Set();
  return {
    t(key, args) {
      if (isKeyModeEnabled()) return key;
      const template = strings[key];
      if (template === void 0) {
        if (options?.onMissingKey && !warnedKeys.has(key)) {
          warnedKeys.add(key);
          options.onMissingKey(key);
        }
        return key;
      }
      return interpolate(template, args);
    }
  };
}
async function fetchLocalizationJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${url}`);
  return await response.json();
}
function resolveLocalizationUrl(template, locale) {
  return template.replace(/\{locale\}/g, locale);
}
var ASP_NET_CULTURE_COOKIE = ".AspNetCore.Culture";
function getAspNetCultureCookie() {
  if (typeof document === "undefined" || !document.cookie) return null;
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.substring(0, eq);
    if (name !== ASP_NET_CULTURE_COOKIE) continue;
    const value = decodeURIComponent(part.substring(eq + 1));
    const cMatch = value.match(/c=([^|]+)/);
    if (cMatch?.[1]) return cMatch[1];
    return value || null;
  }
  return null;
}
function setAspNetCultureCookie(culture, options) {
  if (typeof document === "undefined") return;
  const path = options?.path ?? "/";
  const maxAge = options?.maxAgeSeconds ?? 31536e3;
  const value = encodeURIComponent(`c=${culture}|uic=${culture}`);
  document.cookie = `${ASP_NET_CULTURE_COOKIE}=${value}; path=${path}; max-age=${maxAge}; samesite=lax`;
}
var DEFAULT_URL_LOCALE_PATTERN = /^\/([a-z]{2}(?:-[a-zA-Z]{2,4})?)(?:\/|$)/;
function getLocaleFromUrlPath(pattern = DEFAULT_URL_LOCALE_PATTERN) {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(pattern);
  return match?.[1] ?? null;
}
function shouldPrefixLocale(strategy, locale, defaultLocale) {
  switch (strategy) {
    case "CookieOnly":
      return false;
    case "PathPrefixAll":
      return true;
    default:
      return locale.toLowerCase() !== defaultLocale.toLowerCase();
  }
}
function stripLocalePrefix(pathname, supportedLocales) {
  const trimmed = pathname.replace(/^\/+/, "");
  if (trimmed.length === 0) return "/";
  const slash = trimmed.indexOf("/");
  const first = slash < 0 ? trimmed : trimmed.slice(0, slash);
  const isLocale = supportedLocales.some((l) => l.toLowerCase() === first.toLowerCase());
  if (!isLocale) return pathname.startsWith("/") ? pathname : `/${pathname}`;
  const rest = slash < 0 ? "" : trimmed.slice(slash);
  return rest.length === 0 ? "/" : rest;
}
function buildLocalePath(options) {
  const { strategy, locale, defaultLocale, pathname, supportedLocales } = options;
  const cultureless = stripLocalePrefix(pathname, supportedLocales);
  return shouldPrefixLocale(strategy, locale, defaultLocale) ? `/${locale}${cultureless}` : cultureless;
}
function detectLocale(options) {
  const {
    matchUrl = true,
    urlPattern = DEFAULT_URL_LOCALE_PATTERN,
    matchBrowser = true,
    fallback = "en"
  } = options ?? {};
  if (matchUrl) {
    const fromUrl = getLocaleFromUrlPath(urlPattern);
    if (fromUrl) return fromUrl;
  }
  const fromCookie = getAspNetCultureCookie();
  if (fromCookie) return fromCookie;
  if (matchBrowser && typeof navigator !== "undefined" && navigator.language) {
    const primary = navigator.language.split("-")[0];
    if (primary) return primary;
  }
  return fallback;
}
var MISSING_KEY_FLUSH_DELAY_MS = 75;
var DEFAULT_BUNDLE_SENTINEL = "defaultBundle";
function coerceKeyToToken(key) {
  if (!key) return null;
  const lower = key.toLowerCase();
  if (!lower.startsWith("tables.")) return null;
  const afterTables = lower.substring("tables.".length);
  const firstDot = afterTables.indexOf(".");
  if (firstDot < 0) return `tables.${afterTables}`;
  const tableName = afterTables.substring(0, firstDot);
  const afterTable = afterTables.substring(firstDot + 1);
  if (afterTable.startsWith("views.")) {
    const afterViews = afterTable.substring("views.".length);
    const nextDot = afterViews.indexOf(".");
    const viewId = nextDot < 0 ? afterViews : afterViews.substring(0, nextDot);
    if (!viewId) return null;
    return `views.${viewId}`;
  }
  return `tables.${tableName}`;
}
function coercePrefixToToken(prefix) {
  if (!prefix) return null;
  const lower = prefix.toLowerCase();
  if (lower.startsWith("views.")) {
    const afterViews = lower.substring("views.".length);
    const nextDot = afterViews.indexOf(".");
    const viewId = nextDot < 0 ? afterViews : afterViews.substring(0, nextDot);
    return viewId ? `views.${viewId}` : null;
  }
  return coerceKeyToToken(prefix);
}
var LocaleContext = createContext(null);
LocaleContext.displayName = "Locale";
var LocalizationManifestContext = createContext(null);
LocalizationManifestContext.displayName = "LocalizationManifest";
function useUrlCultureStrategy() {
  return useContext(LocalizationManifestContext)?.urlCultureStrategy;
}
function useDataverseUnavailable() {
  return useContext(LocalizationManifestContext)?.dataverseUnavailable === true;
}
function useDefaultLocale() {
  return useContext(LocalizationManifestContext)?.supportedLocales?.[0];
}
function useLocale() {
  const ctx = useContext(LocaleContext);
  return ctx ?? { locale: "en", setLocale: noopSetLocale };
}
var noopSetLocale = (_) => void 0;
function LocaleProvider({
  initialLocale = "en",
  autoDetect = true,
  urlPattern,
  persistToCookie = true,
  locale: controlledLocale,
  onLocaleChange,
  children
}) {
  const [internalLocale, setInternalLocale] = useState(() => {
    if (!autoDetect) return initialLocale;
    return detectLocale({
      matchUrl: urlPattern !== null,
      ...urlPattern instanceof RegExp && { urlPattern },
      fallback: initialLocale
    });
  });
  const isControlled = controlledLocale !== void 0;
  const locale = isControlled ? controlledLocale : internalLocale;
  const setLocale = useCallback(
    (next) => {
      if (!isControlled) setInternalLocale(next);
      if (persistToCookie) setAspNetCultureCookie(next);
      onLocaleChange?.(next);
    },
    [isControlled, persistToCookie, onLocaleChange]
  );
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return /* @__PURE__ */ jsx(LocaleContext.Provider, { value, children });
}
var STORED_STRINGS_PREFIX = "ppp-localization:";
var STORED_STRINGS_MAX_LENGTH = 2e6;
var STORED_STRINGS_WRITE_DELAY_MS = 500;
function readStoredStrings(locale) {
  try {
    const raw = globalThis.localStorage?.getItem(STORED_STRINGS_PREFIX + locale);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (value?.version !== 1 || typeof value.locale !== "string" || typeof value.bundle !== "string" || typeof value.strings !== "object" || value.strings === null || typeof value.resources !== "object" || value.resources === null) {
      return null;
    }
    return { ...value, overlaid: value.overlaid === true };
  } catch {
    return null;
  }
}
function writeStoredStrings(locale, value) {
  try {
    const raw = JSON.stringify(value);
    if (raw.length > STORED_STRINGS_MAX_LENGTH) return;
    removeStoredStrings(locale);
    globalThis.localStorage?.setItem(STORED_STRINGS_PREFIX + locale, raw);
  } catch {
  }
}
function removeStoredStrings(except) {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return;
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(STORED_STRINGS_PREFIX) && key !== STORED_STRINGS_PREFIX + except) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
  }
}
function stringsWith(defaults, resources) {
  const strings = { ...defaults };
  for (const resource of Object.values(resources)) Object.assign(strings, resource.strings);
  return strings;
}
function storedStringsOf(stored) {
  return stringsWith(stored.strings, stored.resources);
}
function storedTokensOf(stored) {
  return stored ? [DEFAULT_BUNDLE_SENTINEL, ...Object.keys(stored.resources)] : [];
}
function thumbprintOf(thumbs, token) {
  if (token.startsWith("tables.")) return thumbs.tables[token.substring("tables.".length)];
  if (token.startsWith("views.")) return thumbs.views[token.substring("views.".length)];
  return void 0;
}
function defaultMissingKeyWarning(key) {
  console.warn(`PowerPortalsPro: missing localization key "${key}"`);
}
function DefaultLocalizerProvider({
  urls,
  url,
  onMissingKey = defaultMissingKeyWarning,
  prefixes,
  storeStrings = true,
  children
}) {
  const { locale } = useLocale();
  const [seed] = useState(() => storeStrings ? readStoredStrings(locale) : null);
  const resourcesRef = useRef(null);
  const storedRef = useRef(null);
  if (resourcesRef.current === null) {
    resourcesRef.current = seed ? { ...seed.resources } : {};
    storedRef.current = seed ? { ...seed, resources: resourcesRef.current } : null;
  }
  const [cache, setCache] = useState(() => seed ? storedStringsOf(seed) : {});
  const [supportedLocales, setSupportedLocales] = useState(void 0);
  const [urlCultureStrategy, setUrlCultureStrategy] = useState(
    void 0
  );
  const [dataverseUnavailable, setDataverseUnavailable] = useState(void 0);
  const { ppp } = usePowerPortalsPro();
  const storeStringsRef = useRef(storeStrings);
  storeStringsRef.current = storeStrings;
  const writeTimerRef = useRef(null);
  const writeStored = useCallback((locale2 = localeRef.current) => {
    if (writeTimerRef.current !== null) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    if (storedRef.current && storeStringsRef.current) writeStoredStrings(locale2, storedRef.current);
  }, []);
  const scheduleWrite = useCallback(() => {
    if (writeTimerRef.current === null) writeTimerRef.current = setTimeout(() => writeStored(), STORED_STRINGS_WRITE_DELAY_MS);
  }, [writeStored]);
  useEffect(() => () => {
    if (writeTimerRef.current !== null) writeStored();
  }, [writeStored]);
  useEffect(() => {
    if (!storeStrings) removeStoredStrings();
  }, [storeStrings]);
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const pppRef = useRef(ppp);
  pppRef.current = ppp;
  const resolvedLocaleRef = useRef(null);
  const thumbprintsRef = useRef(null);
  const pendingTokensRef = useRef(/* @__PURE__ */ new Set());
  const triedTokensRef = useRef(new Set(seed ? Object.keys(seed.resources) : []));
  const pendingTimerRef = useRef(null);
  const settledTokensRef = useRef(new Set(storedTokensOf(seed)));
  const listenersRef = useRef(/* @__PURE__ */ new Set());
  const baselinePrefixesRef = useRef([]);
  const baselineTokensRef = useRef([]);
  const baselinePrefixesKey = useMemo(() => {
    const input = prefixes ?? [];
    if (input.length === 0) {
      baselinePrefixesRef.current = [];
      baselineTokensRef.current = [];
      return "";
    }
    const unique = Array.from(new Set(input));
    unique.sort();
    baselinePrefixesRef.current = unique;
    const tokens = [];
    for (const p of unique) {
      const t = coercePrefixToToken(p);
      if (t !== null) tokens.push(t);
    }
    baselineTokensRef.current = tokens;
    return unique.join("\n");
  }, [prefixes]);
  const notifyListeners = useCallback(() => {
    const snapshot = Array.from(listenersRef.current);
    for (const listener of snapshot) listener();
  }, []);
  const previousRunRef = useRef(null);
  const flushPendingRef = useRef(() => void 0);
  const overlayTemplates = urls ?? (url !== void 0 ? [url] : []);
  const resolvedOverlayUrls = overlayTemplates.map((t) => resolveLocalizationUrl(t, locale));
  const overlayUrlsKey = resolvedOverlayUrls.join("\n");
  useEffect(() => {
    let canceled = false;
    const previous = previousRunRef.current;
    const isLocaleOrUrlChange = previous !== null && (previous.locale !== locale || previous.overlayKey !== overlayUrlsKey);
    previousRunRef.current = { locale, overlayKey: overlayUrlsKey };
    if (isLocaleOrUrlChange) {
      if (writeTimerRef.current !== null) writeStored(previous.locale);
      const stored = storeStringsRef.current ? readStoredStrings(locale) : null;
      resourcesRef.current = stored ? { ...stored.resources } : {};
      storedRef.current = stored ? { ...stored, resources: resourcesRef.current } : null;
      if (stored) setCache(storedStringsOf(stored));
      setSupportedLocales(void 0);
      pendingTokensRef.current = /* @__PURE__ */ new Set();
      triedTokensRef.current = new Set(stored ? Object.keys(stored.resources) : []);
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      settledTokensRef.current = new Set(storedTokensOf(stored));
      notifyListeners();
    }
    resolvedLocaleRef.current = null;
    thumbprintsRef.current = null;
    const overlayList = overlayUrlsKey.split("\n").filter((u) => u.length > 0);
    (async () => {
      try {
        const guessedLocale = storedRef.current?.locale ?? locale;
        const guessedThumbs = pppRef.current.retrieveLocalizationThumbprintsAsync(guessedLocale);
        guessedThumbs.catch(() => void 0);
        const manifest = await pppRef.current.retrieveLocalizationBundleManifestAsync();
        if (canceled) return;
        setSupportedLocales(manifest.supportedLocales);
        setUrlCultureStrategy(manifest.urlCultureStrategy);
        setDataverseUnavailable(manifest.dataverseUnavailable === true);
        const resolvedLocale = manifest.supportedLocales.includes(locale) ? locale : manifest.supportedLocales[0] ?? locale;
        const thumbs = resolvedLocale === guessedLocale ? await guessedThumbs : await pppRef.current.retrieveLocalizationThumbprintsAsync(resolvedLocale);
        if (canceled) return;
        resolvedLocaleRef.current = resolvedLocale;
        thumbprintsRef.current = thumbs;
        const stored = storedRef.current?.locale === resolvedLocale ? storedRef.current : null;
        let changed = !stored;
        if (!stored) {
          for (const token of Object.keys(resourcesRef.current)) {
            triedTokensRef.current.delete(token);
            pendingTokensRef.current.add(token);
          }
          resourcesRef.current = {};
        } else {
          for (const [token, resource] of Object.entries(resourcesRef.current)) {
            const thumbprint = thumbprintOf(thumbs, token);
            if (thumbprint === void 0) {
              delete resourcesRef.current[token];
              settledTokensRef.current.delete(token);
              changed = true;
            } else if (thumbprint !== resource.thumbprint) {
              triedTokensRef.current.delete(token);
              pendingTokensRef.current.add(token);
            }
          }
        }
        let defaultStrings;
        if (stored && stored.bundle === thumbs.bundle && !stored.overlaid && overlayList.length === 0) {
          defaultStrings = stored.strings;
        } else {
          changed = true;
          const [bundleData, ...overlayDatas] = await Promise.all([
            pppRef.current.retrieveLocalizationBundleAsync(resolvedLocale, thumbs.bundle),
            ...overlayList.map(fetchLocalizationJson)
          ]);
          if (canceled) return;
          defaultStrings = {};
          Object.assign(defaultStrings, flattenStrings(bundleData));
          for (const data of overlayDatas) {
            Object.assign(defaultStrings, flattenStrings(data));
          }
        }
        storedRef.current = {
          version: 1,
          locale: resolvedLocale,
          bundle: thumbs.bundle,
          overlaid: overlayList.length > 0,
          strings: defaultStrings,
          resources: resourcesRef.current
        };
        if (changed) {
          setCache(stringsWith(defaultStrings, resourcesRef.current));
          writeStored();
        }
      } catch (e) {
        if (canceled) return;
        console.error(
          "PowerPortalsPro: failed to load localization bundle; staying on key-fallback localizer.",
          e
        );
      }
      if (canceled) return;
      settledTokensRef.current.add(DEFAULT_BUNDLE_SENTINEL);
      for (const token of baselineTokensRef.current) {
        if (triedTokensRef.current.has(token)) continue;
        if (pendingTokensRef.current.has(token)) continue;
        pendingTokensRef.current.add(token);
      }
      if (pendingTokensRef.current.size > 0 && pendingTimerRef.current === null) {
        pendingTimerRef.current = setTimeout(
          () => flushPendingRef.current(),
          MISSING_KEY_FLUSH_DELAY_MS
        );
      }
      notifyListeners();
    })();
    return () => {
      canceled = true;
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, [locale, overlayUrlsKey]);
  const flushPending = useCallback(async () => {
    pendingTimerRef.current = null;
    const tokensToFetch = Array.from(pendingTokensRef.current);
    if (tokensToFetch.length === 0) return;
    pendingTokensRef.current = /* @__PURE__ */ new Set();
    for (const tok of tokensToFetch) {
      triedTokensRef.current.add(tok);
    }
    const thumbs = thumbprintsRef.current;
    const requestLocale = resolvedLocaleRef.current;
    if (!thumbs || !requestLocale) {
      for (const tok of tokensToFetch) {
        triedTokensRef.current.delete(tok);
        pendingTokensRef.current.add(tok);
      }
      return;
    }
    const fetches = tokensToFetch.map(async (token) => {
      try {
        if (token.startsWith("tables.")) {
          const tableName = token.substring("tables.".length);
          const thumb = thumbs.tables[tableName];
          if (!thumb) return null;
          const data = await pppRef.current.retrieveLocalizationTableBundleAsync(
            tableName,
            requestLocale,
            thumb
          );
          return { token, thumbprint: thumb, data };
        }
        if (token.startsWith("views.")) {
          const viewId = token.substring("views.".length);
          const thumb = thumbs.views[viewId];
          if (!thumb) return null;
          const data = await pppRef.current.retrieveLocalizationViewBundleAsync(
            viewId,
            requestLocale,
            thumb
          );
          return { token, thumbprint: thumb, data };
        }
        return null;
      } catch (e) {
        console.warn(`PowerPortalsPro: localization fetch failed for token ${token}`, e);
        triedTokensRef.current.delete(token);
        return null;
      }
    });
    const responses = await Promise.all(fetches);
    if (requestLocale !== resolvedLocaleRef.current) return;
    let settledChanged = false;
    for (const tok of tokensToFetch) {
      if (!settledTokensRef.current.has(tok)) {
        settledTokensRef.current.add(tok);
        settledChanged = true;
      }
    }
    const merged = {};
    for (const response of responses) {
      if (response === null) continue;
      const strings = flattenStrings(response.data);
      Object.assign(merged, strings);
      resourcesRef.current[response.token] = { thumbprint: response.thumbprint, strings };
    }
    if (Object.keys(merged).length > 0) {
      setCache((prev) => ({ ...prev, ...merged }));
      scheduleWrite();
    }
    if (settledChanged) {
      notifyListeners();
    }
  }, [notifyListeners, scheduleWrite]);
  flushPendingRef.current = flushPending;
  useEffect(() => {
    if (!settledTokensRef.current.has(DEFAULT_BUNDLE_SENTINEL)) return;
    let added = false;
    for (const token of baselineTokensRef.current) {
      if (triedTokensRef.current.has(token)) continue;
      if (pendingTokensRef.current.has(token)) continue;
      pendingTokensRef.current.add(token);
      added = true;
    }
    if (pendingTokensRef.current.size > 0 && pendingTimerRef.current === null) {
      pendingTimerRef.current = setTimeout(flushPending, MISSING_KEY_FLUSH_DELAY_MS);
    }
    if (added) notifyListeners();
  }, [baselinePrefixesKey, flushPending, notifyListeners]);
  const queueMissingKey = useCallback(
    (key) => {
      const token = coerceKeyToToken(key);
      if (token === null) return;
      if (triedTokensRef.current.has(token)) return;
      if (pendingTokensRef.current.has(token)) return;
      pendingTokensRef.current.add(token);
      if (pendingTimerRef.current === null) {
        pendingTimerRef.current = setTimeout(flushPending, MISSING_KEY_FLUSH_DELAY_MS);
      }
    },
    [flushPending]
  );
  const onMissingKeyCombined = useCallback(
    (key) => {
      onMissingKey(key);
      queueMissingKey(key);
    },
    [onMissingKey, queueMissingKey]
  );
  const localizer = useMemo(
    () => createLocalizer(cache, { onMissingKey: onMissingKeyCombined }),
    [cache, onMissingKeyCombined]
  );
  const prefetcher = useMemo(
    () => ({
      prefetch(prefixes2) {
        for (const prefix of prefixes2) {
          const token = coercePrefixToToken(prefix);
          if (token === null) continue;
          if (triedTokensRef.current.has(token)) continue;
          if (pendingTokensRef.current.has(token)) continue;
          pendingTokensRef.current.add(token);
        }
        if (pendingTokensRef.current.size > 0 && pendingTimerRef.current === null) {
          pendingTimerRef.current = setTimeout(flushPending, MISSING_KEY_FLUSH_DELAY_MS);
        }
      },
      isLoaded(prefixes2) {
        if (!settledTokensRef.current.has(DEFAULT_BUNDLE_SENTINEL)) return false;
        for (const token of baselineTokensRef.current) {
          if (!settledTokensRef.current.has(token)) return false;
        }
        for (const prefix of prefixes2) {
          const token = coercePrefixToToken(prefix);
          if (token === null) continue;
          if (!settledTokensRef.current.has(token)) return false;
        }
        return true;
      },
      subscribe(listener) {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      }
    }),
    [flushPending]
  );
  const manifestContextValue = useMemo(
    () => ({ supportedLocales, urlCultureStrategy, dataverseUnavailable }),
    [supportedLocales, urlCultureStrategy, dataverseUnavailable]
  );
  return /* @__PURE__ */ jsx(LocalizationManifestContext.Provider, { value: manifestContextValue, children: /* @__PURE__ */ jsx(LocalizationPrefetcherContext.Provider, { value: prefetcher, children: /* @__PURE__ */ jsx(LocalizerProvider, { value: localizer, children }) }) });
}
var UnsavedChangesRegistryContext = createContext(null);
UnsavedChangesRegistryContext.displayName = "UnsavedChangesRegistry";
function UnsavedChangesRegistryProvider({ children }) {
  const reportersRef = useRef(/* @__PURE__ */ new Set());
  const listenersRef = useRef(/* @__PURE__ */ new Set());
  const notify = useCallback(() => {
    for (const listener of listenersRef.current) listener();
  }, []);
  const register = useCallback(
    (reporter) => {
      reportersRef.current.add(reporter);
      notify();
      return () => {
        reportersRef.current.delete(reporter);
        notify();
      };
    },
    [notify]
  );
  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);
  const isAnyDirty = useCallback(() => {
    for (const reporter of reportersRef.current) {
      if (reporter.isDirty()) return true;
    }
    return false;
  }, []);
  const registry = useMemo(
    () => ({ register, subscribe, isAnyDirty, notifyDirtyChanged: notify }),
    [register, subscribe, isAnyDirty, notify]
  );
  return /* @__PURE__ */ jsx(UnsavedChangesRegistryContext.Provider, { value: registry, children });
}
function useUnsavedChangesRegistry() {
  return useContext(UnsavedChangesRegistryContext);
}
function useIsAnyDirty() {
  const registry = useUnsavedChangesRegistry();
  const subscribe = useCallback(
    (listener) => {
      if (!registry) return () => {
      };
      return registry.subscribe(listener);
    },
    [registry]
  );
  const getSnapshot = useCallback(() => {
    if (!registry) return false;
    return registry.isAnyDirty();
  }, [registry]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
function PowerPortalsProProvider({
  children,
  transport,
  localizationUrl,
  localizationUrls,
  localizationPrefixes,
  localizationStoreStrings,
  tableMetadataCache,
  viewMetadataCache,
  viewsForTableCache,
  organizationSettingsCache
}) {
  const transportRef = useRef(null);
  if (transportRef.current === null) {
    transportRef.current = transport ?? new Transport();
  }
  const transportInstance = transportRef.current;
  const auth = useMemo(() => new AuthClient(transportInstance), [transportInstance]);
  const ppp = useMemo(() => new PowerPortalsProClient(transportInstance), [transportInstance]);
  const resolvedTableMetadataCache = useMemo(
    () => tableMetadataCache ?? new InMemoryTableMetadataCache(ppp),
    [ppp, tableMetadataCache]
  );
  const resolvedViewMetadataCache = useMemo(
    () => viewMetadataCache ?? new InMemoryViewMetadataCache(ppp),
    [ppp, viewMetadataCache]
  );
  const resolvedViewsForTableCache = useMemo(
    () => viewsForTableCache ?? new InMemoryViewsForTableCache(ppp),
    [ppp, viewsForTableCache]
  );
  const recordPagesCache = useMemo(() => new RecordPagesCache(ppp), [ppp]);
  const resolvedOrganizationSettingsCache = useMemo(
    () => organizationSettingsCache ?? new InMemoryOrganizationSettingsCache(ppp),
    [ppp, organizationSettingsCache]
  );
  const [authState, setAuthState] = useState({ status: AuthStatus.Loading });
  const [timeZone, setTimeZone] = useState(void 0);
  const signedInAs = authState.status === AuthStatus.Authenticated ? `user:${authState.user.userId ?? ""}` : authState.status === AuthStatus.Anonymous ? AuthStatus.Anonymous : null;
  const metadataCachedFor = useRef(null);
  useEffect(() => {
    if (signedInAs === null) return;
    if (metadataCachedFor.current !== null && metadataCachedFor.current !== signedInAs) {
      resolvedTableMetadataCache.clear();
      resolvedViewMetadataCache.clear();
      resolvedViewsForTableCache.clear();
    }
    metadataCachedFor.current = signedInAs;
  }, [signedInAs, resolvedTableMetadataCache, resolvedViewMetadataCache, resolvedViewsForTableCache]);
  const refreshAuth = useCallback(async () => {
    try {
      const me = await auth.getCurrentUserAsync();
      setTimeZone(me.timeZone ?? void 0);
      if (me.isAuthenticated) {
        setAuthState({ status: AuthStatus.Authenticated, user: me });
      } else {
        setAuthState({ status: AuthStatus.Anonymous });
      }
    } catch {
      setAuthState({ status: AuthStatus.Anonymous });
    }
  }, [auth]);
  const login = useCallback(
    async (request) => {
      const response = await auth.loginAsync(request);
      if (response.result === LoginResult.Success) {
        await refreshAuth();
      }
      return response;
    },
    [auth, refreshAuth]
  );
  const loginWithPasskey = useCallback(async () => {
    const response = await auth.signInWithPasskeyAsync();
    if (response.result === LoginResult.Success) {
      await refreshAuth();
    }
    return response;
  }, [auth, refreshAuth]);
  const logout = useCallback(async () => {
    await auth.logoutAsync();
    setAuthState({ status: AuthStatus.Anonymous });
    try {
      setTimeZone((await auth.getCurrentUserAsync()).timeZone ?? void 0);
    } catch {
    }
  }, [auth]);
  const switchIdentity = useCallback(async () => {
    const response = await auth.switchIdentityAsync();
    if (response.result === SwitchIdentityResult.Switched) {
      await refreshAuth();
    }
    return response;
  }, [auth, refreshAuth]);
  const impersonate = useCallback(async (targetUserId) => {
    const response = await auth.impersonateAsync(targetUserId);
    if (response.result === ImpersonateResult.Started) {
      await refreshAuth();
    }
    return response;
  }, [auth, refreshAuth]);
  const stopImpersonation = useCallback(async () => {
    const response = await auth.stopImpersonationAsync();
    if (response.result === StopImpersonationResult.Stopped || response.result === StopImpersonationResult.ImpersonatorNotFound) {
      await refreshAuth();
    }
    return response;
  }, [auth, refreshAuth]);
  const searchImpersonationTargets = useCallback(
    (query) => auth.searchImpersonationTargetsAsync(query),
    [auth]
  );
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);
  useEffect(() => {
    if (typeof document !== "undefined") writeBrowserTimeZoneCookie(document);
  }, []);
  const value = useMemo(
    () => ({
      transport: transportInstance,
      auth,
      ppp,
      tableMetadataCache: resolvedTableMetadataCache,
      viewMetadataCache: resolvedViewMetadataCache,
      viewsForTableCache: resolvedViewsForTableCache,
      organizationSettingsCache: resolvedOrganizationSettingsCache,
      recordPagesCache,
      authState,
      timeZone,
      refreshAuth,
      login,
      loginWithPasskey,
      logout,
      switchIdentity,
      impersonate,
      stopImpersonation,
      searchImpersonationTargets
    }),
    [
      transportInstance,
      auth,
      ppp,
      resolvedTableMetadataCache,
      resolvedViewMetadataCache,
      resolvedViewsForTableCache,
      resolvedOrganizationSettingsCache,
      recordPagesCache,
      authState,
      timeZone,
      refreshAuth,
      login,
      loginWithPasskey,
      logout,
      switchIdentity,
      impersonate,
      stopImpersonation,
      searchImpersonationTargets
    ]
  );
  let localizationContent;
  if (localizationUrl === false) {
    localizationContent = children;
  } else {
    const overlayUrls = [];
    if (typeof localizationUrl === "string") overlayUrls.push(localizationUrl);
    if (localizationUrls) overlayUrls.push(...localizationUrls);
    localizationContent = /* @__PURE__ */ jsxs(LocaleProvider, { children: [
      /* @__PURE__ */ jsx(CacheLocaleSync, {}),
      /* @__PURE__ */ jsx(
        DefaultLocalizerProvider,
        {
          ...overlayUrls.length > 0 && { urls: overlayUrls },
          ...localizationPrefixes !== void 0 && { prefixes: localizationPrefixes },
          ...localizationStoreStrings !== void 0 && { storeStrings: localizationStoreStrings },
          children
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsx(PowerPortalsProContext.Provider, { value, children: /* @__PURE__ */ jsx(UnsavedChangesRegistryProvider, { children: localizationContent }) });
}
function CacheLocaleSync() {
  const { locale } = useLocale();
  const {
    tableMetadataCache,
    viewMetadataCache,
    viewsForTableCache,
    organizationSettingsCache
  } = usePowerPortalsPro();
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    tableMetadataCache.clear();
    viewMetadataCache.clear();
    viewsForTableCache.clear();
    organizationSettingsCache.invalidate();
  }, [
    locale,
    tableMetadataCache,
    viewMetadataCache,
    viewsForTableCache,
    organizationSettingsCache
  ]);
  return null;
}

// src/use-auth.ts
function useAuth() {
  const {
    authState,
    refreshAuth,
    login,
    loginWithPasskey,
    logout,
    switchIdentity,
    impersonate,
    stopImpersonation,
    searchImpersonationTargets
  } = usePowerPortalsPro();
  return {
    ...authState,
    refresh: refreshAuth,
    login,
    loginWithPasskey,
    logout,
    switchIdentity,
    impersonate,
    stopImpersonation,
    searchImpersonationTargets
  };
}

// src/use-user-time-zone.ts
function useUserTimeZone() {
  return usePowerPortalsPro().timeZone;
}
function holdsAny(held, required) {
  const names = typeof required === "string" ? [required] : required;
  if (names.length === 0) return true;
  const lowered = held.map((r) => r.toLowerCase());
  return names.some((name) => lowered.includes(name.toLowerCase()));
}
function hasPortalRole(user, roles) {
  return holdsAny(user?.portalRoles ?? [], roles);
}
function hasSecurityRole(user, roles) {
  return holdsAny(user?.securityRoles ?? [], roles);
}
function hasWebRole(user, roles) {
  return holdsAny(user?.webRoles ?? [], roles);
}
function hasRole(user, roles) {
  return holdsAny(user?.roles ?? [], roles);
}
var BUILT_IN_SYSTEM_ADMIN_ROLE = "System Administrator";
function isSystemAdministrator(user, nominatedRoleName) {
  if (user?.portalUserType !== PortalUserType.SystemUser) return false;
  if (hasSecurityRole(user, BUILT_IN_SYSTEM_ADMIN_ROLE)) return true;
  const nominated = nominatedRoleName?.trim();
  return !!nominated && hasSecurityRole(user, nominated);
}
function AuthorizedView({
  children,
  roles,
  portalRoles,
  securityRoles,
  webRoles,
  fallback = null
}) {
  const auth = useAuth();
  if (auth.status !== AuthStatus.Authenticated) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  if (roles !== void 0 && !hasRole(auth.user, roles)) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  if (portalRoles !== void 0 && !hasPortalRole(auth.user, portalRoles)) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  if (securityRoles !== void 0 && !hasSecurityRole(auth.user, securityRoles)) {
    return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  }
  if (webRoles !== void 0 && !hasWebRole(auth.user, webRoles)) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  return /* @__PURE__ */ jsx(Fragment, { children });
}
var QueryStatus = {
  Loading: "loading",
  Success: "success",
  Error: "error"
};
function useAsyncResource(fetcher, deps, enabled = true, keepPreviousData = false) {
  const [state, setState] = useState({
    status: enabled ? QueryStatus.Loading : QueryStatus.Success,
    data: void 0,
    error: void 0,
    isFetching: enabled
  });
  const [refetchTick, setRefetchTick] = useState(0);
  const pendingResolversRef = useRef([]);
  useEffect(
    () => () => {
      const resolvers = pendingResolversRef.current;
      pendingResolversRef.current = [];
      for (const r of resolvers) r.resolve();
    },
    []
  );
  useEffect(() => {
    if (!enabled) {
      setState({
        status: QueryStatus.Success,
        data: void 0,
        error: void 0,
        isFetching: false
      });
      const resolvers2 = pendingResolversRef.current;
      pendingResolversRef.current = [];
      for (const r of resolvers2) r.resolve();
      return;
    }
    const resolvers = pendingResolversRef.current;
    pendingResolversRef.current = [];
    const controller = new AbortController();
    setState((prev) => {
      if (keepPreviousData && prev.status === QueryStatus.Success && prev.data !== void 0) {
        if (prev.isFetching && prev.error === void 0) return prev;
        return { ...prev, error: void 0, isFetching: true };
      }
      if (prev.status === QueryStatus.Loading && prev.data === void 0 && prev.error === void 0 && prev.isFetching) {
        return prev;
      }
      return {
        status: QueryStatus.Loading,
        data: void 0,
        error: void 0,
        isFetching: true
      };
    });
    fetcher(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) {
          setState({
            status: QueryStatus.Success,
            data,
            error: void 0,
            isFetching: false
          });
        }
        for (const r of resolvers) r.resolve();
      },
      (err) => {
        if (controller.signal.aborted || err instanceof Error && err.name === "AbortError") {
          for (const r of resolvers) r.resolve();
          return;
        }
        setState({
          status: QueryStatus.Error,
          data: void 0,
          error: err,
          isFetching: false
        });
        for (const r of resolvers) r.reject(err);
      }
    );
    return () => {
      controller.abort();
      for (const r of resolvers) r.resolve();
    };
  }, [...deps, enabled, refetchTick]);
  const refetch = useCallback(() => {
    return new Promise((resolve, reject) => {
      pendingResolversRef.current.push({ resolve, reject });
      setRefetchTick((n) => n + 1);
    });
  }, []);
  return { ...state, refetch };
}
var _sharedAuthOptions = /* @__PURE__ */ new WeakMap();
function sharedAuthOptions(auth, identity) {
  let byIdentity = _sharedAuthOptions.get(auth);
  if (!byIdentity) {
    byIdentity = /* @__PURE__ */ new Map();
    _sharedAuthOptions.set(auth, byIdentity);
  }
  const existing = byIdentity.get(identity);
  if (existing) return existing;
  const request = auth.getAuthOptionsAsync().catch((reason) => {
    byIdentity.delete(identity);
    throw reason;
  });
  byIdentity.set(identity, request);
  return request;
}
function evictAuthOptions(auth, identity) {
  _sharedAuthOptions.get(auth)?.delete(identity);
}
function useAuthOptions(options) {
  const { auth: client } = usePowerPortalsPro();
  const auth = useAuth();
  const enabled = options?.enabled ?? true;
  const identity = auth.status === AuthStatus.Authenticated ? auth.user.userId ?? "signed-in" : "anonymous";
  const result = useAsyncResource(() => sharedAuthOptions(client, identity), [client, identity], enabled);
  return useMemo(
    () => ({
      ...result,
      refetch: () => {
        evictAuthOptions(client, identity);
        return result.refetch();
      }
    }),
    [result, client, identity]
  );
}

// src/use-is-system-admin.ts
function useIsSystemAdmin() {
  return useIsSystemAdminStatus().isSystemAdmin;
}
function useIsSystemAdminStatus() {
  const auth = useAuth();
  const authenticated = auth.status === AuthStatus.Authenticated;
  const { data: authOptions, status } = useAuthOptions({ enabled: authenticated });
  if (auth.status === AuthStatus.Loading) return { isSystemAdmin: false, isLoading: true, isIndeterminate: false };
  if (!authenticated) return { isSystemAdmin: false, isLoading: false, isIndeterminate: false };
  const settled = status === QueryStatus.Error || authOptions !== void 0;
  if (!settled) return { isSystemAdmin: false, isLoading: true, isIndeterminate: false };
  return {
    isSystemAdmin: isSystemAdministrator(auth.user, authOptions?.systemAdminRoleName),
    isLoading: false,
    // Settled without the options means the fetch failed. The built-in role still answers, so
    // this is not a loading state — but it is not a reliable "no" either, and a page that draws
    // a denial from it would be refusing an administrator it simply could not identify.
    isIndeterminate: authOptions === void 0
  };
}
function SystemAdminView({ children, fallback = null }) {
  return /* @__PURE__ */ jsx(Fragment, { children: useIsSystemAdmin() ? children : fallback });
}

// src/use-record.ts
function useRecord(tableLogicalName, recordId, options) {
  const { ppp } = usePowerPortalsPro();
  const columns = options?.columns;
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    (signal) => ppp.retrieveRecordAsync(tableLogicalName, recordId, columns, signal),
    // Joining the columns array is the cheapest way to get a stable dep without
    // forcing the consumer to memoize. Column lists are short (typically <20).
    [ppp, tableLogicalName, recordId, columns?.join(",") ?? ""],
    enabled
  );
}

// src/use-fetch-records.ts
function useFetchRecords(fetchXml, options) {
  const { ppp } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    (signal) => ppp.retrieveRecordsAsync(fetchXml, signal),
    [ppp, fetchXml],
    enabled
  );
}

// src/use-query-data.ts
function useQueryData(request, options) {
  const { ppp } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  const keepPreviousData = options?.keepPreviousData ?? false;
  const requestKey = JSON.stringify(request);
  return useAsyncResource(
    (signal) => ppp.loadQueryAsync(request, signal),
    [ppp, requestKey],
    enabled,
    keepPreviousData
  );
}
function highlightMatches(highlight, value) {
  if (highlight.rangeSize !== void 0) {
    const start = numberOf(highlight.value);
    if (start !== void 0) {
      const number = numberOf(value);
      return number !== void 0 && inRange(number, start, highlight.rangeSize);
    }
  }
  return value === highlight.value;
}
function inRange(number, start, size) {
  const scale = 10 ** Math.min(10, Math.max(decimalPlacesOf(number), decimalPlacesOf(start), decimalPlacesOf(size)));
  const scaled = Math.round(number * scale);
  const from = Math.round(start * scale);
  return scaled >= from && scaled < from + Math.round(size * scale);
}
function decimalPlacesOf(n) {
  const match = /(?:\.(\d+))?(?:e([+-]\d+))?$/i.exec(String(n));
  return Math.max(0, (match?.[1]?.length ?? 0) - Number(match?.[2] ?? 0));
}
function numberOf(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : void 0;
  }
  return void 0;
}
function reduce(state, action) {
  switch (action.type) {
    // Filter / sort / search / pageSize changes invalidate the current
    // page — the new result set has different rows, the user expects to
    // land on page 1. Mirrors what `<MainGrid>` does for standalone
    // mode. Highlight changes DON'T reset the page (purely client-side).
    case "setFilter":
      return { ...state, filter: action.filter, page: 1 };
    case "setSort":
      return { ...state, sort: action.sort, page: 1 };
    case "setSearch":
      return { ...state, search: action.search, page: 1 };
    case "setPage":
      return { ...state, page: action.page };
    case "setPageSize":
      return { ...state, pageSize: action.pageSize, page: 1 };
    case "setHighlight":
      return { ...state, highlight: action.highlight };
    // A new grouping orders the rows anew, every group expanded; collapsing one keeps the page.
    case "setGroupBy":
      return groupBySignature(action.groupBy) === groupBySignature(state.groupBy) ? state : { ...state, groupBy: action.groupBy, collapsedGroups: EMPTY_KEYS, page: 1 };
    case "setCollapsedGroups":
      return [...action.keys].sort().join("") === [...state.collapsedGroups].sort().join("") ? state : { ...state, collapsedGroups: action.keys };
  }
}
function pagingScopeOf(request) {
  const { pageNumber: _pageNumber, pagingCookie: _pagingCookie, aggregations: _aggregations, ...scope } = request;
  return JSON.stringify(scope);
}
function groupBySignature(groupBy) {
  return groupBy.map((level) => `${level.columnName.trim().toLowerCase()}|${level.descending ?? false}|${level.dateGrouping ?? 0}|${level.rangeSize ?? ""}`).join("/");
}
function useViewDataSource(options) {
  const debounceMs = options.debounceMs ?? 300;
  const enabled = options.enabled ?? true;
  const keepPreviousData = options.keepPreviousData ?? true;
  const [state, dispatch] = useReducer(reduce, void 0, () => ({
    filter: options.initialFilter ?? EMPTY_FILTERS,
    sort: options.initialSort ?? EMPTY_SORTS,
    search: options.initialSearch ?? "",
    page: options.initialPage ?? 1,
    pageSize: options.initialPageSize ?? 50,
    highlight: options.initialHighlight,
    groupBy: options.initialGroupBy ?? EMPTY_GROUP_BY,
    collapsedGroups: EMPTY_KEYS
  }));
  const aggregationFingerprint = useMemo(() => {
    if (!options.aggregations || options.aggregations.length === 0) return null;
    return JSON.stringify({
      viewId: options.viewId,
      fetchXml: options.fetchXml,
      staticFilters: options.staticFilters,
      filter: state.filter,
      search: state.search,
      aggregations: options.aggregations
    });
  }, [
    options.viewId,
    options.fetchXml,
    options.staticFilters,
    options.aggregations,
    state.filter,
    state.search
  ]);
  const lastFetchedAggregationFingerprintRef = useRef(null);
  const cachedAggregationResultsRef = useRef(EMPTY_AGGREGATIONS);
  const [pagingCookies] = useState(() => new PagingCookies());
  const request = useMemo(() => {
    const filters = [
      ...options.staticFilters ?? [],
      ...state.filter
    ];
    const includeAggregations = aggregationFingerprint !== null && aggregationFingerprint !== lastFetchedAggregationFingerprintRef.current;
    const rows2 = {
      ...options.viewId !== void 0 && { viewId: options.viewId },
      ...options.fetchXml !== void 0 && { fetchXml: options.fetchXml },
      // No sort of its own keeps the view's: an empty list would tell the server to drop it.
      ...state.sort.length > 0 && { sorts: state.sort },
      pageNumber: state.page,
      pageSize: state.pageSize,
      ...state.search !== "" && { searchText: state.search },
      ...filters.length > 0 && { filters },
      ...includeAggregations && options.aggregations && {
        aggregations: options.aggregations
      },
      ...options.relatedRecords && options.relatedRecords.length > 0 && {
        relatedRecords: options.relatedRecords
      },
      ...options.additionalColumns && options.additionalColumns.length > 0 && {
        additionalColumns: [...options.additionalColumns]
      },
      // Grouped: the rows ordered by their groups, the collapsed groups' records left out.
      ...state.groupBy.length > 0 && { groupBy: [...state.groupBy] },
      ...state.groupBy.length > 0 && state.collapsedGroups.length > 0 && {
        collapsedGroups: [...state.collapsedGroups]
      },
      ...state.groupBy.length > 0 && options.maximumRangedValues !== void 0 && {
        maximumRangedValues: options.maximumRangedValues
      }
    };
    const pagingCookie = state.groupBy.length > 0 ? void 0 : pagingCookies.cookieBeside(pagingScopeOf(rows2), state.page);
    return pagingCookie ? { ...rows2, pagingCookie } : rows2;
  }, [
    options.viewId,
    options.fetchXml,
    options.staticFilters,
    options.aggregations,
    options.relatedRecords,
    options.additionalColumns,
    options.maximumRangedValues,
    state.filter,
    state.sort,
    state.search,
    state.page,
    state.pageSize,
    state.groupBy,
    state.collapsedGroups,
    aggregationFingerprint
  ]);
  const debouncedRequest = useDebouncedValue(request, debounceMs);
  const currentQuery = useMemo(() => {
    const { pageNumber: _pageNumber, pageSize: _pageSize, pagingCookie: _pagingCookie, aggregations: _aggregations, ...rowSet } = debouncedRequest;
    return rowSet;
  }, [debouncedRequest]);
  const query = useQueryData(debouncedRequest, { enabled, keepPreviousData });
  const setFilter = useCallback((filter) => {
    dispatch({ type: "setFilter", filter });
  }, []);
  const setSort = useCallback((sort) => {
    dispatch({ type: "setSort", sort });
  }, []);
  const setSearch = useCallback((search) => {
    dispatch({ type: "setSearch", search });
  }, []);
  const setPage = useCallback((page) => {
    dispatch({ type: "setPage", page });
  }, []);
  const setPageSize = useCallback((pageSize) => {
    dispatch({ type: "setPageSize", pageSize });
  }, []);
  const setHighlight = useCallback((highlight) => {
    dispatch({ type: "setHighlight", highlight });
  }, []);
  const setGroupBy = useCallback((groupBy) => {
    dispatch({ type: "setGroupBy", groupBy });
  }, []);
  const setCollapsedGroups = useCallback((keys) => {
    dispatch({ type: "setCollapsedGroups", keys });
  }, []);
  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query.refetch]);
  const data = query.data;
  useEffect(() => {
    if (!data) return;
    pagingCookies.record(pagingScopeOf(debouncedRequest), Number(debouncedRequest.pageNumber ?? 1), data.pagingInfo?.pagingCookie);
  }, [data]);
  const rows = data?.tableRecords ?? EMPTY_RECORDS;
  const fetchedColumns = data?.columns ?? EMPTY_COLUMNS;
  const groupabilityRef = useRef(EMPTY_COLUMNS);
  const columns = useMemo(() => {
    const kept = keepingGroupability(fetchedColumns, groupabilityRef.current);
    if (kept.length > 0) groupabilityRef.current = kept;
    return kept;
  }, [fetchedColumns]);
  const totalCount = data?.pagingInfo?.totalRecordCount;
  const moreRecords = data?.pagingInfo?.moreRecords;
  const tablePermissions = data?.tablePermissions;
  const aggregations = options.aggregations ?? EMPTY_AGGREGATION_SPEC;
  const isFetchingAggregations = query.isFetching && debouncedRequest.aggregations !== void 0 && debouncedRequest.aggregations !== null && debouncedRequest.aggregations.length > 0;
  const lastSeenDataRef = useRef(void 0);
  useEffect(() => {
    if (data === lastSeenDataRef.current) return;
    lastSeenDataRef.current = data;
    if (data?.aggregations) {
      cachedAggregationResultsRef.current = data.aggregations;
      lastFetchedAggregationFingerprintRef.current = aggregationFingerprint;
    }
  }, [data, aggregationFingerprint]);
  const aggregationResults = (options.aggregations?.length ?? 0) === 0 ? EMPTY_AGGREGATIONS : data?.aggregations ?? cachedAggregationResultsRef.current;
  return useMemo(
    () => ({
      filter: state.filter,
      sort: state.sort,
      search: state.search,
      page: state.page,
      pageSize: state.pageSize,
      highlight: state.highlight,
      groupBy: state.groupBy,
      collapsedGroups: state.collapsedGroups,
      aggregations,
      currentQuery,
      status: query.status,
      rows,
      totalCount,
      moreRecords,
      columns,
      tablePermissions,
      aggregationResults,
      error: query.error,
      isFetching: query.isFetching,
      isFetchingAggregations,
      setFilter,
      setSort,
      setSearch,
      setPage,
      setPageSize,
      setHighlight,
      setGroupBy,
      setCollapsedGroups,
      refresh
    }),
    [
      state.filter,
      state.sort,
      state.search,
      state.page,
      state.pageSize,
      state.highlight,
      state.groupBy,
      state.collapsedGroups,
      aggregations,
      currentQuery,
      query.status,
      rows,
      totalCount,
      moreRecords,
      columns,
      tablePermissions,
      aggregationResults,
      query.error,
      query.isFetching,
      isFetchingAggregations,
      setFilter,
      setSort,
      setSearch,
      setPage,
      setPageSize,
      setHighlight,
      setGroupBy,
      setCollapsedGroups,
      refresh
    ]
  );
}
var EMPTY_FILTERS = [];
var EMPTY_SORTS = [];
var EMPTY_RECORDS = [];
var EMPTY_COLUMNS = [];
var EMPTY_AGGREGATIONS = [];
var EMPTY_AGGREGATION_SPEC = [];
var EMPTY_GROUP_BY = [];
var EMPTY_KEYS = [];
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  const valueKey = JSON.stringify(value);
  const previousKeyRef = useRef(valueKey);
  useEffect(() => {
    if (delayMs === 0) {
      setDebounced(value);
      previousKeyRef.current = valueKey;
      return;
    }
    if (previousKeyRef.current === valueKey) return;
    const timer = setTimeout(() => {
      setDebounced(value);
      previousKeyRef.current = valueKey;
    }, delayMs);
    return () => clearTimeout(timer);
  }, [valueKey, delayMs]);
  return debounced;
}

// src/use-table-metadata.ts
function useTableMetadata(tableLogicalName, options) {
  const { tableMetadataCache } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    async () => {
      const result = await tableMetadataCache.getAsync(tableLogicalName);
      return result ?? void 0;
    },
    [tableMetadataCache, tableLogicalName],
    enabled
  );
}

// src/use-table-permissions.ts
function useTablePermissions(tableLogicalName, options) {
  const { ppp } = usePowerPortalsPro();
  const enabled = (options?.enabled ?? true) && tableLogicalName.length > 0;
  return useAsyncResource(
    async () => ppp.retrieveTablePermissionsAsync(tableLogicalName),
    [ppp, tableLogicalName],
    enabled
  );
}
var answers = /* @__PURE__ */ new WeakMap();
var forgotten = /* @__PURE__ */ new Set();
function keyOf(auth) {
  if (auth.status === "loading") return void 0;
  if (auth.status === "anonymous") return "";
  const roles = (auth.user.roles ?? []).map((role) => role.toLowerCase()).sort();
  return `${auth.user.userId ?? ""}${roles.join("")}`;
}
function grantedFor(ppp, key) {
  const kept = answers.get(ppp);
  if (kept && kept.key === key) return kept.request;
  const entry = { key, request: ppp.retrieveOperationAccessAsync() };
  answers.set(ppp, entry);
  entry.request.catch(() => {
    if (answers.get(ppp) === entry) answers.delete(ppp);
  });
  return entry.request;
}
function forgetOperationAccess(ppp) {
  answers.delete(ppp);
  for (const listener of [...forgotten]) listener();
}
function useOperationAccess(operation) {
  const { ppp, authState } = usePowerPortalsPro();
  const key = keyOf(authState);
  const [answer, setAnswer] = useState();
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const listener = () => setGeneration((x) => x + 1);
    forgotten.add(listener);
    return () => {
      forgotten.delete(listener);
    };
  }, []);
  useEffect(() => {
    if (key === void 0 || !operation.trim()) return;
    let live = true;
    const name = operation.trim().toLowerCase();
    grantedFor(ppp, key).then(
      (granted) => {
        if (!live) return;
        const match = Object.entries(granted).find(([x]) => x.toLowerCase() === name);
        setAnswer({ key, operation, grants: match?.[1] ?? [] });
      },
      () => {
        if (live) setAnswer({ key, operation, grants: [] });
      }
    );
    return () => {
      live = false;
    };
  }, [ppp, key, operation, generation]);
  const grants = answer && answer.key === key && answer.operation === operation ? answer.grants : void 0;
  const askAgain = useCallback(() => forgetOperationAccess(ppp), [ppp]);
  return { grants, runsAlone: operationRunsAlone(grants), askAgain };
}
function useRecordPages(options) {
  const { recordPagesCache, authState } = usePowerPortalsPro();
  const user = authState.status === AuthStatus.Authenticated ? `user:${authState.user.userId ?? ""}` : authState.status === AuthStatus.Anonymous ? null : void 0;
  const enabled = (options?.enabled ?? true) && user !== void 0 && recordPagesCache !== void 0;
  const [loaded, setLoaded] = useState();
  const [retries, setRetries] = useState(0);
  useEffect(() => {
    if (!enabled || user === void 0 || recordPagesCache === void 0) return;
    const abort = new AbortController();
    let retry;
    recordPagesCache.getAsync(user, abort.signal).then(
      (pages) => {
        if (abort.signal.aborted) return;
        setLoaded({ user, pages });
        if (recordPagesCache.hasFailed(user)) retry = setTimeout(() => setRetries((count) => count + 1), recordPagesCache.retryMs);
      },
      // Only this caller giving up rejects; the cache answers a failure with no pages.
      () => {
      }
    );
    return () => {
      abort.abort();
      if (retry !== void 0) clearTimeout(retry);
    };
  }, [recordPagesCache, user, enabled, retries]);
  return enabled && loaded !== void 0 && loaded.user === user ? loaded.pages : void 0;
}
function useRecordPage(table, options) {
  const pages = useRecordPages({ enabled: (options?.enabled ?? true) && !!table });
  return findRecordPage(pages, table);
}

// src/use-view-metadata.ts
function useViewMetadata(viewId, options) {
  const { viewMetadataCache } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    async () => {
      const result = await viewMetadataCache.getAsync(viewId);
      return result ?? void 0;
    },
    [viewMetadataCache, viewId],
    enabled
  );
}

// src/use-views-for-table.ts
function useViewsForTable(tableLogicalName, options) {
  const { viewsForTableCache } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    () => viewsForTableCache.getAsync(tableLogicalName),
    [viewsForTableCache, tableLogicalName],
    enabled
  );
}

// src/use-organization-settings.ts
function useOrganizationSettings(options) {
  const { organizationSettingsCache } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  return useAsyncResource(
    async () => {
      const result = await organizationSettingsCache.getAsync();
      return result ?? void 0;
    },
    [organizationSettingsCache],
    enabled
  );
}
function useSupportedLocales(options) {
  const { ppp } = usePowerPortalsPro();
  const enabled = options?.enabled ?? true;
  const manifestState = useContext(LocalizationManifestContext);
  const providerScoped = manifestState !== null;
  const fallback = useAsyncResource(
    async () => {
      const manifest = await ppp.retrieveLocalizationBundleManifestAsync();
      return manifest.supportedLocales;
    },
    [ppp],
    enabled && !providerScoped
  );
  if (providerScoped) {
    if (manifestState.supportedLocales === void 0) {
      return {
        status: enabled ? QueryStatus.Loading : QueryStatus.Success,
        data: void 0,
        error: void 0,
        isFetching: enabled,
        refetch: fallback.refetch
      };
    }
    return {
      status: QueryStatus.Success,
      data: manifestState.supportedLocales,
      error: void 0,
      isFetching: false,
      refetch: fallback.refetch
    };
  }
  return fallback;
}
function useQueryParam(name) {
  const [value, setValue] = useState(() => readParam(name));
  useEffect(() => {
    if (typeof window === "undefined") return void 0;
    installHistoryPatch();
    setValue(readParam(name));
    const handler = () => setValue(readParam(name));
    window.addEventListener("popstate", handler);
    window.addEventListener(PUSH_EVENT, handler);
    window.addEventListener(REPLACE_EVENT, handler);
    return () => {
      window.removeEventListener("popstate", handler);
      window.removeEventListener(PUSH_EVENT, handler);
      window.removeEventListener(REPLACE_EVENT, handler);
    };
  }, [name]);
  return value;
}
function replaceQueryParam(name, value) {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if ((params.get(name) ?? void 0) === value) return;
  if (value === void 0) params.delete(name);
  else params.set(name, value);
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
  );
}
var PUSH_EVENT = "ppp:pushstate";
var REPLACE_EVENT = "ppp:replacestate";
var historyPatched = false;
function installHistoryPatch() {
  if (historyPatched) return;
  if (typeof window === "undefined" || typeof window.history === "undefined") return;
  historyPatched = true;
  const origPush = window.history.pushState.bind(window.history);
  const origReplace = window.history.replaceState.bind(window.history);
  window.history.pushState = function(...args) {
    origPush(...args);
    window.dispatchEvent(new Event(PUSH_EVENT));
  };
  window.history.replaceState = function(...args) {
    origReplace(...args);
    window.dispatchEvent(new Event(REPLACE_EVENT));
  };
}
function readParam(name) {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  } catch {
    return null;
  }
}
function useBeforeUnloadWhen(condition) {
  useEffect(() => {
    if (!condition) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [condition]);
}
var MainContextContext = createContext(null);
MainContextContext.displayName = "MainContext";
function MainContext({
  children,
  onBeforeSave,
  warnOnUnsavedChanges,
  forceSuccessfulValidationBeforeSave
}) {
  const parentMainContext = useContext(MainContextContext);
  const isTopMost = parentMainContext === null;
  const resolvedWarnOnUnsavedChanges = warnOnUnsavedChanges ?? parentMainContext?.warnOnUnsavedChanges ?? true;
  const resolvedForceSuccessfulValidationBeforeSave = forceSuccessfulValidationBeforeSave ?? parentMainContext?.forceSuccessfulValidationBeforeSave ?? true;
  const descendantsRef = useRef(/* @__PURE__ */ new Set());
  const [version, setVersion] = useState(0);
  const { ppp } = usePowerPortalsPro();
  const refreshAll = useCallback(async () => {
    for (const descendant of descendantsRef.current) {
      await descendant.refresh();
    }
  }, []);
  const resetState = useCallback(() => {
    for (const descendant of descendantsRef.current) {
      descendant.resetState();
    }
  }, []);
  const validate = useCallback(async () => {
    for (const descendant of descendantsRef.current) {
      const ok = await descendant.validate();
      if (!ok) return false;
    }
    return true;
  }, []);
  const applyPendingEdits = useCallback(async () => {
    for (const descendant of [...descendantsRef.current]) {
      await descendant.applyPendingEdits?.();
    }
  }, []);
  const saveAll = useCallback(async () => {
    await applyPendingEdits();
    if (onBeforeSave) await onBeforeSave();
    if (resolvedForceSuccessfulValidationBeforeSave) {
      const valid = await validate();
      if (!valid) return;
    }
    const requests = [];
    for (const descendant of descendantsRef.current) {
      requests.push(...descendant.getRequests());
    }
    const hasAfterSaveWork = [...descendantsRef.current].some(
      (descendant) => descendant.afterSave !== void 0 && descendant.isDirty()
    );
    if (requests.length === 0 && !hasAfterSaveWork) return;
    if (requests.length > 0) {
      const listening = [...descendantsRef.current].filter((descendant) => descendant.saved !== void 0);
      const responses = await ppp.executeMultipleAsync(requests, { returnResponses: listening.length > 0 });
      for (const descendant of listening) descendant.saved?.(responses);
    }
    for (const descendant of descendantsRef.current) {
      await descendant.afterSave?.(void 0);
    }
    await refreshAll();
  }, [applyPendingEdits, onBeforeSave, validate, ppp, refreshAll, resolvedForceSuccessfulValidationBeforeSave]);
  const registerDescendant = useCallback((descendant) => {
    descendantsRef.current.add(descendant);
    setVersion((v) => v + 1);
    return () => {
      descendantsRef.current.delete(descendant);
      setVersion((v) => v + 1);
    };
  }, []);
  const notifyDescendantDirtyChanged = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);
  const isDirty = useMemo(() => {
    for (const descendant of descendantsRef.current) {
      if (descendant.isDirty()) return true;
    }
    return false;
  }, [version]);
  const getRequests = useCallback(() => {
    const all = [];
    for (const descendant of descendantsRef.current) {
      all.push(...descendant.getRequests());
    }
    return all;
  }, []);
  const value = useMemo(
    () => ({
      saveAll,
      refreshAll,
      resetState,
      validate,
      getRequests,
      registerDescendant,
      applyPendingEdits,
      notifyDescendantDirtyChanged,
      isDirty,
      warnOnUnsavedChanges: resolvedWarnOnUnsavedChanges,
      forceSuccessfulValidationBeforeSave: resolvedForceSuccessfulValidationBeforeSave
    }),
    [
      saveAll,
      refreshAll,
      resetState,
      validate,
      getRequests,
      registerDescendant,
      applyPendingEdits,
      notifyDescendantDirtyChanged,
      isDirty,
      resolvedWarnOnUnsavedChanges,
      resolvedForceSuccessfulValidationBeforeSave
    ]
  );
  const afterSaveNested = useCallback(async () => {
    for (const descendant of descendantsRef.current) {
      await descendant.afterSave?.(void 0);
    }
  }, []);
  useEffect(() => {
    if (!parentMainContext) return;
    return parentMainContext.registerDescendant({
      getRequests,
      validate,
      refresh: refreshAll,
      afterSave: afterSaveNested,
      resetState,
      isDirty: () => isDirtyRef.current
    });
  }, [parentMainContext?.registerDescendant, getRequests, validate, refreshAll, resetState]);
  const parentMainContextRef = useRef(parentMainContext);
  parentMainContextRef.current = parentMainContext;
  useEffect(() => {
    parentMainContextRef.current?.notifyDescendantDirtyChanged();
  }, [isDirty]);
  useBeforeUnloadWhen(isTopMost && resolvedWarnOnUnsavedChanges && isDirty);
  const unsavedChangesRegistry = useUnsavedChangesRegistry();
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const resolvedWarnRef = useRef(resolvedWarnOnUnsavedChanges);
  resolvedWarnRef.current = resolvedWarnOnUnsavedChanges;
  useEffect(() => {
    if (!isTopMost) return;
    if (!unsavedChangesRegistry) return;
    return unsavedChangesRegistry.register({
      isDirty: () => resolvedWarnRef.current && isDirtyRef.current
    });
  }, [isTopMost, unsavedChangesRegistry]);
  useEffect(() => {
    if (!isTopMost) return;
    if (!unsavedChangesRegistry) return;
    unsavedChangesRegistry.notifyDirtyChanged();
  }, [isTopMost, unsavedChangesRegistry, isDirty, resolvedWarnOnUnsavedChanges]);
  return /* @__PURE__ */ jsx(MainContextContext.Provider, { value, children });
}
function useMainContext() {
  return useContext(MainContextContext);
}
function MainContextProvider({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(MainContextContext.Provider, { value, children });
}
var OverlayContext = createContext(null);
OverlayContext.displayName = "OverlayService";
function useOverlayService() {
  return useContext(OverlayContext)?.service ?? null;
}
function useOverlayForTarget(targetId) {
  const ctx = useContext(OverlayContext);
  if (!ctx) return null;
  const stack = ctx.overlaysByTarget.get(targetId ?? "") ?? [];
  return stack[stack.length - 1] ?? null;
}
function OverlayServiceProvider({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(OverlayContext.Provider, { value, children });
}
function useOverlayServiceState() {
  const [overlaysByTarget, setOverlaysByTarget] = useState(() => /* @__PURE__ */ new Map());
  const nextIdRef = useRef(1);
  const service = useMemo(
    () => ({
      show(options) {
        const id = `overlay-${nextIdRef.current++}`;
        const instance = { id, ...options };
        setOverlaysByTarget((prev) => {
          const key = options?.targetId ?? "";
          const next = new Map(prev);
          next.set(key, [...next.get(key) ?? [], instance]);
          return next;
        });
        return instance;
      },
      hide(overlay) {
        setOverlaysByTarget((prev) => {
          const key = overlay.targetId ?? "";
          const stack = prev.get(key) ?? [];
          const idx = stack.findIndex((o) => o.id === overlay.id);
          if (idx < 0) return prev;
          const next = new Map(prev);
          if (stack.length === 1) {
            next.delete(key);
          } else {
            next.set(key, [...stack.slice(0, idx), ...stack.slice(idx + 1)]);
          }
          return next;
        });
      },
      hideAll() {
        setOverlaysByTarget(/* @__PURE__ */ new Map());
      }
    }),
    []
  );
  return useMemo(() => ({ service, overlaysByTarget }), [service, overlaysByTarget]);
}
async function runWithOverlayAsync(service, options, operation) {
  if (!service) return operation();
  const handle = service.show(options);
  try {
    return await operation();
  } finally {
    service.hide(handle);
  }
}
function useRunWithOverlay() {
  const service = useOverlayService();
  return useCallback(
    (options, operation) => runWithOverlayAsync(service, options, operation),
    [service]
  );
}
var ValidationContextContext = createContext(null);
ValidationContextContext.displayName = "ValidationContext";
function useValidationContext() {
  return useContext(ValidationContextContext);
}
function useColumnValidationErrors(columnName) {
  const ctx = useValidationContext();
  if (!ctx) return void 0;
  return ctx.errors.get(columnName);
}
function ValidationProvider({ children }) {
  const validatorsRef = useRef(/* @__PURE__ */ new Map());
  const [errors, setErrors] = useState(
    () => /* @__PURE__ */ new Map()
  );
  const register = useCallback(
    (columnName, validator) => {
      validatorsRef.current.set(columnName, validator);
      return () => {
        validatorsRef.current.delete(columnName);
        setErrors((prev) => {
          if (!prev.has(columnName)) return prev;
          const next = new Map(prev);
          next.delete(columnName);
          return next;
        });
      };
    },
    []
  );
  const validateColumn = useCallback(async (columnName) => {
    const validator = validatorsRef.current.get(columnName);
    if (!validator) return;
    const fieldErrors = await validator();
    setErrors((prev) => {
      const existing = prev.get(columnName) ?? [];
      if (existing.length === fieldErrors.length && existing.every((e, i) => e === fieldErrors[i])) {
        return prev;
      }
      const next = new Map(prev);
      if (fieldErrors.length === 0) {
        next.delete(columnName);
      } else {
        next.set(columnName, fieldErrors);
      }
      return next;
    });
  }, []);
  const validateAll = useCallback(async () => {
    const next = /* @__PURE__ */ new Map();
    for (const [col, validator] of validatorsRef.current) {
      const fieldErrors = await validator();
      if (fieldErrors.length > 0) {
        next.set(col, fieldErrors);
      }
    }
    setErrors(next);
    return next.size === 0;
  }, []);
  const clearErrors = useCallback(() => {
    setErrors(/* @__PURE__ */ new Map());
  }, []);
  const isValid = errors.size === 0;
  const value = useMemo(
    () => ({ register, validateColumn, validateAll, clearErrors, isValid, errors }),
    [register, validateColumn, validateAll, clearErrors, isValid, errors]
  );
  return /* @__PURE__ */ jsx(ValidationContextContext.Provider, { value, children });
}
function valuesEqual(a, b) {
  const aEmpty = a === null || a === void 0;
  const bEmpty = b === null || b === void 0;
  if (aEmpty && bEmpty) return true;
  if (aEmpty !== bEmpty) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
var GUID_PATTERN = /^\{?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\}?$/i;
function parseQueryDefault(metadata, rawValue) {
  switch (metadata.$type) {
    // StringMetadata + MemoMetadata both ride on StringValue. The TS
    // discriminators are distinct so we list both arms.
    case 14:
    case 7:
      return { $type: 14, value: rawValue };
    case 5: {
      const n = Number(rawValue);
      return Number.isFinite(n) && Number.isInteger(n) ? { $type: 5, value: n } : null;
    }
    case 18: {
      const n = Number(rawValue);
      return Number.isFinite(n) && Number.isInteger(n) ? { $type: 18, value: n } : null;
    }
    case 3: {
      const n = Number(rawValue);
      return Number.isFinite(n) ? { $type: 3, value: n } : null;
    }
    case 4: {
      const n = Number(rawValue);
      return Number.isFinite(n) ? { $type: 4, value: n } : null;
    }
    case 8: {
      const n = Number(rawValue);
      return Number.isFinite(n) ? { $type: 8, value: n } : null;
    }
    case 0: {
      const lower = rawValue.trim().toLowerCase();
      if (lower === "true") return { $type: 0, value: true };
      if (lower === "false") return { $type: 0, value: false };
      return null;
    }
    case 2: {
      const ts = Date.parse(rawValue);
      if (Number.isNaN(ts)) return null;
      return { $type: 2, value: new Date(ts).toISOString() };
    }
    // A choice, and the state and status reason columns that are kinds of it.
    case 11:
    case 12:
    case 13: {
      const n = Number(rawValue);
      return Number.isFinite(n) && Number.isInteger(n) ? { $type: 11, value: n } : null;
    }
    case 40: {
      const parts = rawValue.split(",").map((s) => s.trim()).filter(Boolean);
      const values = [];
      for (const p of parts) {
        const n = Number(p);
        if (Number.isFinite(n) && Number.isInteger(n)) values.push({ value: n });
      }
      return values.length > 0 ? { $type: 40, value: values } : null;
    }
    case 6: {
      try {
        const parsed = JSON.parse(rawValue);
        if (!parsed || typeof parsed !== "object") return null;
        const findKey = (target) => Object.keys(parsed).find(
          (k) => k.toLowerCase() === target.toLowerCase()
        );
        const obj = parsed;
        const tableNameKey = findKey("tableName");
        const valueKey = findKey("value");
        const nameKey = findKey("name");
        const tableName = tableNameKey ? obj[tableNameKey] : void 0;
        const value = valueKey ? obj[valueKey] : void 0;
        const name = nameKey ? obj[nameKey] : null;
        if (typeof tableName !== "string" || tableName.length === 0) return null;
        if (typeof value !== "string" || value.length === 0) return null;
        return {
          $type: 6,
          tableName,
          value,
          name: typeof name === "string" ? name : null
        };
      } catch {
        return null;
      }
    }
    case 15: {
      if (!GUID_PATTERN.test(rawValue)) return null;
      return { $type: 15, value: rawValue.replace(/[{}]/g, "") };
    }
    default:
      return null;
  }
}
function parseQueryDefaultsFromUrl(search, tableMetadata) {
  const out = {};
  if (!search) return out;
  const params = new URLSearchParams(search);
  for (const [columnName, rawValue] of params) {
    if (!columnName) continue;
    const meta = tableMetadata.columns.find(
      (c) => c.columnName === columnName
    );
    if (!meta) continue;
    try {
      const parsed = parseQueryDefault(meta, rawValue);
      if (parsed) out[columnName] = parsed;
    } catch {
    }
  }
  return out;
}
var RecordContextContext = createContext(null);
RecordContextContext.displayName = "RecordContext";
function withoutUnsettableDefaultCurrency(record) {
  const seeded = record.properties?.["transactioncurrencyid"];
  if (!seeded || !record.currency || seeded.value !== record.currency.id || canWriteColumn(record, "transactioncurrencyid"))
    return record;
  const properties = { ...record.properties };
  delete properties["transactioncurrencyid"];
  return { ...record, properties };
}
function withChanges(record, changes) {
  const properties = { ...record.properties ?? {} };
  for (const [col, val] of changes) {
    if (val === null) {
      delete properties[col];
    } else {
      properties[col] = val;
    }
  }
  return { ...record, properties };
}
function RecordContext(props) {
  return /* @__PURE__ */ jsx(ValidationProvider, { children: /* @__PURE__ */ jsx(RecordContextInner, { ...props }) });
}
function RecordContextInner({
  children,
  table,
  id: idProp,
  queryParameterName,
  initialRecord,
  initialRecordChanged = false,
  loadedRecord: loadedRecordProp,
  initialPendingChanges,
  columns,
  onRecordLoaded,
  onBeforeSave,
  onBeforeDelete,
  warnOnUnsavedChanges,
  forceSuccessfulValidationBeforeSave,
  isolated = false
}) {
  const queryId = useQueryParam(queryParameterName ?? "");
  const resolvedId = queryParameterName && queryId !== null && queryId.length > 0 ? queryId : idProp;
  const propIsEmptyId = !resolvedId || resolvedId === "00000000-0000-0000-0000-000000000000";
  const [effectiveId, setEffectiveId] = useState(
    propIsEmptyId ? void 0 : resolvedId
  );
  useEffect(() => {
    setEffectiveId(propIsEmptyId ? void 0 : resolvedId);
  }, [resolvedId, propIsEmptyId]);
  const isCreateMode = effectiveId === void 0;
  const isSkipFetchMode = !isCreateMode && loadedRecordProp !== void 0;
  const { ppp, organizationSettingsCache } = usePowerPortalsPro();
  const outerMainContext = useMainContext();
  const mainContext = isolated ? null : outerMainContext;
  const validation = useValidationContext();
  const isTopMost = mainContext === null;
  const resolvedWarnOnUnsavedChanges = warnOnUnsavedChanges ?? mainContext?.warnOnUnsavedChanges ?? true;
  const resolvedForceSuccessfulValidationBeforeSave = forceSuccessfulValidationBeforeSave ?? mainContext?.forceSuccessfulValidationBeforeSave ?? true;
  const overlayService = useOverlayService();
  const localizer = useLocalizer();
  const overlayTargetId = useId();
  const query = useRecord(table, effectiveId ?? "", {
    ...columns ? { columns } : {},
    enabled: !isCreateMode && !isSkipFetchMode
  });
  const metadataQuery = useTableMetadata(table);
  const [loadedRecord, setLoadedRecord] = useState(
    () => isSkipFetchMode ? loadedRecordProp : void 0
  );
  const [pendingChanges, setPendingChanges] = useState(
    () => /* @__PURE__ */ new Map()
  );
  const onRecordLoadedRef = useRef(onRecordLoaded);
  onRecordLoadedRef.current = onRecordLoaded;
  const onBeforeSaveRef = useRef(onBeforeSave);
  onBeforeSaveRef.current = onBeforeSave;
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;
  const recordRef = useRef(void 0);
  const loadedRecordRef = useRef(void 0);
  const ownDirtyRef = useRef(false);
  const pendingChangesRef = useRef(/* @__PURE__ */ new Map());
  pendingChangesRef.current = pendingChanges;
  const validationRef = useRef(validation);
  validationRef.current = validation;
  const isCreateModeRef = useRef(isCreateMode);
  isCreateModeRef.current = isCreateMode;
  const fetchesRecordRef = useRef(!isCreateMode && !isSkipFetchMode);
  fetchesRecordRef.current = !isCreateMode && !isSkipFetchMode;
  const forceSuccessfulValidationBeforeSaveRef = useRef(
    resolvedForceSuccessfulValidationBeforeSave
  );
  forceSuccessfulValidationBeforeSaveRef.current = resolvedForceSuccessfulValidationBeforeSave;
  const queryParameterNameRef = useRef(queryParameterName);
  queryParameterNameRef.current = queryParameterName;
  const isTopMostRef = useRef(mainContext === null);
  isTopMostRef.current = mainContext === null;
  useEffect(() => {
    if (!isCreateMode) return;
    const changed = initialRecordChanged ? initialRecord?.properties : void 0;
    const baseSeed = initialRecord ? changed ? { ...initialRecord, properties: {} } : initialRecord : { tableName: table, properties: {} };
    const seeded = baseSeed.id !== void 0 ? baseSeed : { ...baseSeed, _idForCreate: crypto.randomUUID() };
    setLoadedRecord(seeded);
    setPendingChanges(changed ? new Map(Object.entries(changed)) : /* @__PURE__ */ new Map());
  }, [isCreateMode, initialRecord, initialRecordChanged, table]);
  useEffect(() => {
    if (!isCreateMode) return;
    if (!queryParameterName) return;
    if (!metadataQuery.data) return;
    if (!loadedRecord) return;
    if (pendingChangesRef.current.size > 0) return;
    const search = typeof window !== "undefined" ? window.location.search : "";
    const defaults = parseQueryDefaultsFromUrl(search, metadataQuery.data);
    const cols = Object.keys(defaults);
    if (cols.length === 0) return;
    let changed = false;
    const nextProperties = {
      ...loadedRecord.properties ?? {}
    };
    for (const col of cols) {
      const next = defaults[col];
      if (!valuesEqual(nextProperties[col], next)) {
        nextProperties[col] = next;
        changed = true;
      }
    }
    if (!changed) return;
    setLoadedRecord(
      (current) => current ? { ...current, properties: nextProperties } : current
    );
  }, [isCreateMode, queryParameterName, metadataQuery.data, loadedRecord]);
  useEffect(() => {
    if (!isCreateMode) return;
    if (!metadataQuery.data) return;
    if (!loadedRecord) return;
    if (pendingChangesRef.current.size > 0) return;
    if (loadedRecord.currency) return;
    let canceled = false;
    (async () => {
      try {
        const settings = await organizationSettingsCache.getAsync();
        if (canceled) return;
        const defaultCurrency = settings?.defaultCurrency;
        if (!defaultCurrency) return;
        const tableHasCurrencyLookup = metadataQuery.data.columns?.some((c) => c.columnName === "transactioncurrencyid") ?? false;
        setLoadedRecord((current) => {
          if (!current) return current;
          if (current.currency) return current;
          const nextProperties = {
            ...current.properties ?? {}
          };
          if (tableHasCurrencyLookup && !nextProperties["transactioncurrencyid"] && canWriteColumn(current, "transactioncurrencyid")) {
            nextProperties["transactioncurrencyid"] = {
              $type: 6,
              // ColumnType.Lookup — matches the discriminator on LookupValue
              value: defaultCurrency.id,
              tableName: "transactioncurrency",
              name: defaultCurrency.name
            };
          }
          return { ...current, currency: defaultCurrency, properties: nextProperties };
        });
      } catch {
      }
    })();
    return () => {
      canceled = true;
    };
  }, [isCreateMode, metadataQuery.data, loadedRecord, organizationSettingsCache]);
  const seedIdentity = isCreateMode ? loadedRecord?._idForCreate ?? loadedRecord?.id : void 0;
  const [createDenial, setCreateDenial] = useState(void 0);
  const createDenied = seedIdentity !== void 0 && createDenial?.seed === seedIdentity && createDenial.denied;
  useEffect(() => {
    if (seedIdentity === void 0) return;
    const abort = new AbortController();
    (async () => {
      try {
        const permissions = await ppp.retrieveUserTablePermissionsAsync(table, abort.signal);
        if (abort.signal.aborted) return;
        const denied = (permissions.operations & TableSecurityPermission.Create) === 0;
        setCreateDenial({ seed: seedIdentity, denied });
        if (denied) setPendingChanges((current) => current.size === 0 ? current : /* @__PURE__ */ new Map());
        const newRecordColumnPermissions = permissions.newRecordColumnPermissions;
        if (!newRecordColumnPermissions) return;
        const permitted = { columnPermissions: newRecordColumnPermissions };
        setLoadedRecord((current) => {
          if (!current || current.columnPermissions) return current;
          if ((current._idForCreate ?? current.id) !== seedIdentity) return current;
          return withoutUnsettableDefaultCurrency({
            ...current,
            columnPermissions: newRecordColumnPermissions
          });
        });
        setPendingChanges((current) => {
          const dropped = [...current.keys()].filter((column) => !canWriteColumn(permitted, column));
          if (dropped.length === 0) return current;
          const next = new Map(current);
          for (const column of dropped) next.delete(column);
          return next;
        });
      } catch {
      }
    })();
    return () => {
      abort.abort();
    };
  }, [seedIdentity, table, ppp]);
  useEffect(() => {
    if (!isSkipFetchMode || !loadedRecordProp) return;
    setLoadedRecord(loadedRecordProp);
    setPendingChanges(
      initialPendingChanges && initialPendingChanges.size > 0 ? new Map(initialPendingChanges) : /* @__PURE__ */ new Map()
    );
    validationRef.current?.clearErrors();
    onRecordLoadedRef.current?.(loadedRecordProp);
  }, [isSkipFetchMode, loadedRecordProp, initialPendingChanges]);
  useEffect(() => {
    if (query.status === "success" && query.data) {
      setLoadedRecord(query.data);
      setPendingChanges(
        initialPendingChanges && initialPendingChanges.size > 0 ? new Map(initialPendingChanges) : /* @__PURE__ */ new Map()
      );
      validationRef.current?.clearErrors();
      onRecordLoadedRef.current?.(query.data);
    }
  }, [query.status, query.data, initialPendingChanges]);
  loadedRecordRef.current = loadedRecord;
  const record = useMemo(() => {
    if (!loadedRecord) return void 0;
    if (pendingChanges.size === 0) return loadedRecord;
    return { ...withChanges(loadedRecord, pendingChanges), isDirty: true };
  }, [loadedRecord, pendingChanges]);
  const dirtyColumns = useMemo(
    () => new Set(pendingChanges.keys()),
    [pendingChanges]
  );
  const ownDirty = pendingChanges.size > 0;
  recordRef.current = record;
  ownDirtyRef.current = ownDirty;
  const nestedRef = useRef(/* @__PURE__ */ new Set());
  const [nestedVersion, setNestedVersion] = useState(0);
  const nestedDirty = useMemo(() => {
    for (const d of nestedRef.current) {
      if (d.isDirty()) return true;
    }
    return false;
  }, [nestedVersion]);
  const pendingEditsRef = useRef(/* @__PURE__ */ new Set());
  const [pendingEditsVersion, setPendingEditsVersion] = useState(0);
  const pendingEditDirty = useMemo(() => {
    for (const edit of pendingEditsRef.current) {
      if (edit.isPending()) return true;
    }
    return false;
  }, [pendingEditsVersion]);
  const notifyPendingEditChanged = useCallback(() => {
    setPendingEditsVersion((v) => v + 1);
  }, []);
  const registerPendingEdit = useCallback((edit) => {
    pendingEditsRef.current.add(edit);
    setPendingEditsVersion((v) => v + 1);
    return () => {
      pendingEditsRef.current.delete(edit);
      setPendingEditsVersion((v) => v + 1);
    };
  }, []);
  const applyPendingEdits = useCallback(async () => {
    for (const edit of [...pendingEditsRef.current]) {
      if (edit.isPending()) await edit.apply();
    }
    for (const descendant of [...nestedRef.current]) {
      await descendant.applyPendingEdits?.();
    }
  }, []);
  const isDirty = ownDirty || nestedDirty || pendingEditDirty;
  const registerNested = useCallback((descendant) => {
    nestedRef.current.add(descendant);
    setNestedVersion((v) => v + 1);
    return () => {
      nestedRef.current.delete(descendant);
      setNestedVersion((v) => v + 1);
    };
  }, []);
  const notifyNestedDirtyChanged = useCallback(() => {
    setNestedVersion((v) => v + 1);
  }, []);
  const buildOwnSaveRequest = useCallback(() => {
    if (!recordRef.current || !ownDirtyRef.current) return null;
    const current = recordRef.current;
    if (isCreateModeRef.current) {
      const allProperties = {
        ...current.properties
      };
      return {
        $type: "CreateRequest",
        record: {
          tableName: current.tableName,
          // Include the client-pre-generated GUID. The seed effect writes
          // it into `_idForCreate`; the server's in-batch resolver matches
          // AssociateRequest / child-FK references against either `id` or
          // `_idForCreate`, so a Create + Associate pair within one
          // `ExecuteMultiple` resolves to the same record.
          // `id` is also forwarded when the consumer supplied an explicit
          // one via `initialRecord` (rare — pre-assigned ids on data
          // import).
          ...current.id !== void 0 && { id: current.id },
          ...current._idForCreate !== void 0 && current._idForCreate !== null && {
            _idForCreate: current._idForCreate
          },
          properties: allProperties
        }
      };
    }
    const partialProperties = {};
    for (const [col, val] of pendingChangesRef.current) {
      partialProperties[col] = val;
    }
    const partialRecord = {
      tableName: current.tableName,
      properties: partialProperties,
      ...current.id !== void 0 && { id: current.id }
    };
    return { $type: "UpdateRequest", record: partialRecord };
  }, []);
  const getRequests = useCallback(() => {
    const all = [];
    const own = buildOwnSaveRequest();
    if (own) all.push(own);
    for (const descendant of nestedRef.current) {
      all.push(...descendant.getRequests());
    }
    return all;
  }, [buildOwnSaveRequest]);
  const validate = useCallback(async () => {
    for (const descendant of nestedRef.current) {
      if (!await descendant.validate()) return false;
    }
    return true;
  }, []);
  const saveAll = useCallback(async () => {
    if (!loadedRecordRef.current) {
      throw new Error("RecordContext.save called before the record loaded.");
    }
    await applyPendingEdits();
    await onBeforeSaveRef.current?.();
    if (forceSuccessfulValidationBeforeSaveRef.current) {
      const valid = await validate();
      if (!valid) return;
    }
    const ownReq = buildOwnSaveRequest();
    const sentChanges = ownReq ? pendingChangesRef.current : /* @__PURE__ */ new Map();
    const descendantReqs = [];
    for (const descendant of nestedRef.current) {
      descendantReqs.push(...descendant.getRequests());
    }
    const requests = ownReq ? [ownReq, ...descendantReqs] : descendantReqs;
    let savedRecordId = loadedRecordRef.current.id;
    if (requests.length > 0) {
      const responses = await ppp.executeMultipleAsync(requests, { returnResponses: true });
      for (const descendant of nestedRef.current) descendant.saved?.(responses);
      if (ownReq?.$type === "CreateRequest") {
        const ownResp = responses[0];
        if (ownResp?.$type === "CreateResponse" && ownResp.id) {
          savedRecordId = ownResp.id;
          setEffectiveId(ownResp.id);
          const qpn = queryParameterNameRef.current;
          if (qpn && isTopMostRef.current && typeof window !== "undefined") {
            const existing = new URLSearchParams(window.location.search).get(
              qpn
            );
            if (!existing) {
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.set(qpn, ownResp.id);
              window.history.replaceState(
                window.history.state,
                "",
                newUrl.toString()
              );
            }
          }
        }
      }
      if (sentChanges.size > 0 || savedRecordId !== loadedRecordRef.current?.id) {
        const id = savedRecordId;
        setLoadedRecord((current) => {
          if (!current) return current;
          const saved = withChanges(current, sentChanges);
          const formattedValues = { ...saved.formattedValues ?? {} };
          for (const col of sentChanges.keys()) delete formattedValues[col];
          return { ...saved, formattedValues, ...id !== void 0 && { id } };
        });
      }
      setPendingChanges(/* @__PURE__ */ new Map());
    }
    for (const descendant of nestedRef.current) {
      await descendant.afterSave?.(savedRecordId);
    }
    if (!isCreateModeRef.current) {
      void refetchRef.current();
    }
    for (const descendant of nestedRef.current) {
      await descendant.refresh();
    }
  }, [ppp, validate, buildOwnSaveRequest]);
  const reload = useCallback(async () => {
    await Promise.all([
      fetchesRecordRef.current ? refetchRef.current() : Promise.resolve(),
      (async () => {
        for (const descendant of nestedRef.current) {
          await descendant.refresh();
        }
      })()
    ]);
  }, []);
  const resetState = useCallback(() => {
    setPendingChanges(/* @__PURE__ */ new Map());
    validationRef.current?.clearErrors();
    for (const descendant of nestedRef.current) {
      descendant.resetState();
    }
  }, []);
  const setColumnValue = useCallback(
    (columnName, value2) => {
      setPendingChanges((prev) => {
        if (!loadedRecord) return prev;
        const original = loadedRecord.properties?.[columnName];
        const newValue = value2 ?? null;
        if (valuesEqual(original, newValue)) {
          if (!prev.has(columnName)) return prev;
          const next2 = new Map(prev);
          next2.delete(columnName);
          return next2;
        }
        const existing = prev.has(columnName) ? prev.get(columnName) ?? null : void 0;
        if (existing !== void 0 && valuesEqual(existing, newValue)) return prev;
        const next = new Map(prev);
        next.set(columnName, newValue);
        return next;
      });
    },
    [loadedRecord]
  );
  const hydrateColumnValue = useCallback(
    (columnName, value2) => {
      setLoadedRecord((current) => {
        if (!current) return current;
        if (pendingChanges.has(columnName)) return current;
        const previous = current.properties?.[columnName];
        if (value2 === null) {
          if (previous === void 0) return current;
          const nextProperties = { ...current.properties ?? {} };
          delete nextProperties[columnName];
          return { ...current, properties: nextProperties };
        }
        if (valuesEqual(previous, value2)) return current;
        return {
          ...current,
          properties: { ...current.properties ?? {}, [columnName]: value2 }
        };
      });
    },
    [pendingChanges]
  );
  const loadingOverlayRef = useRef(null);
  useEffect(() => {
    if (!overlayService) return;
    if (query.status === "loading") {
      if (!loadingOverlayRef.current) {
        loadingOverlayRef.current = overlayService.show({
          targetId: overlayTargetId,
          description: localizer.t("app.messages.record-loading")
        });
      }
    } else if (loadingOverlayRef.current) {
      overlayService.hide(loadingOverlayRef.current);
      loadingOverlayRef.current = null;
    }
    return () => {
      if (loadingOverlayRef.current) {
        overlayService.hide(loadingOverlayRef.current);
        loadingOverlayRef.current = null;
      }
    };
  }, [overlayService, query.status, overlayTargetId, localizer]);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const mainContextRef = useRef(mainContext);
  mainContextRef.current = mainContext;
  const afterSaveNested = useCallback(async () => {
    const record2 = loadedRecordRef.current;
    const recordId = record2 === void 0 ? void 0 : getRecordReferenceId(record2);
    for (const descendant of nestedRef.current) {
      await descendant.afterSave?.(recordId);
    }
  }, []);
  useEffect(() => {
    if (!mainContext) return;
    return mainContext.registerDescendant({
      getRequests,
      validate,
      refresh: reload,
      afterSave: afterSaveNested,
      applyPendingEdits,
      resetState,
      isDirty: () => isDirtyRef.current
    });
  }, [mainContext?.registerDescendant, getRequests, validate, reload, afterSaveNested, applyPendingEdits, resetState]);
  useEffect(() => {
    mainContextRef.current?.notifyDescendantDirtyChanged();
  }, [isDirty]);
  useBeforeUnloadWhen(isTopMost && resolvedWarnOnUnsavedChanges && isDirty);
  const unsavedChangesRegistry = useUnsavedChangesRegistry();
  const resolvedWarnRef = useRef(resolvedWarnOnUnsavedChanges);
  resolvedWarnRef.current = resolvedWarnOnUnsavedChanges;
  useEffect(() => {
    if (!isTopMost) return;
    if (!unsavedChangesRegistry) return;
    return unsavedChangesRegistry.register({
      isDirty: () => resolvedWarnRef.current && isDirtyRef.current
    });
  }, [isTopMost, unsavedChangesRegistry]);
  useEffect(() => {
    if (!isTopMost) return;
    if (!unsavedChangesRegistry) return;
    unsavedChangesRegistry.notifyDirtyChanged();
  }, [isTopMost, unsavedChangesRegistry, isDirty, resolvedWarnOnUnsavedChanges]);
  const innerMainContext = useMemo(
    () => ({
      saveAll,
      refreshAll: reload,
      resetState,
      validate,
      getRequests,
      registerDescendant: registerNested,
      applyPendingEdits,
      notifyDescendantDirtyChanged: notifyNestedDirtyChanged,
      isDirty,
      warnOnUnsavedChanges: resolvedWarnOnUnsavedChanges,
      forceSuccessfulValidationBeforeSave: resolvedForceSuccessfulValidationBeforeSave
    }),
    [
      saveAll,
      reload,
      resetState,
      validate,
      getRequests,
      registerNested,
      applyPendingEdits,
      notifyNestedDirtyChanged,
      isDirty,
      resolvedWarnOnUnsavedChanges,
      resolvedForceSuccessfulValidationBeforeSave
    ]
  );
  const onBeforeDeleteRef = useRef(onBeforeDelete);
  onBeforeDeleteRef.current = onBeforeDelete;
  const runBeforeDelete = useCallback(async () => {
    const handler = onBeforeDeleteRef.current;
    if (handler) await handler();
  }, []);
  const value = useMemo(
    () => ({
      status: query.status,
      record,
      error: query.error,
      reload,
      registerPendingEdit,
      notifyPendingEditChanged,
      save: saveAll,
      getRequests,
      resetState,
      setColumnValue,
      hydrateColumnValue,
      tableMetadata: metadataQuery.data,
      isDirty,
      dirtyColumns,
      createDenied,
      overlayTargetId,
      runBeforeDelete
    }),
    [
      query.status,
      query.error,
      record,
      reload,
      registerPendingEdit,
      notifyPendingEditChanged,
      saveAll,
      getRequests,
      resetState,
      setColumnValue,
      hydrateColumnValue,
      metadataQuery.data,
      isDirty,
      dirtyColumns,
      createDenied,
      overlayTargetId,
      runBeforeDelete
    ]
  );
  return /* @__PURE__ */ jsx(MainContextProvider, { value: innerMainContext, children: /* @__PURE__ */ jsx(RecordContextContext.Provider, { value, children }) });
}
function useRecordContext() {
  const ctx = useContext(RecordContextContext);
  if (!ctx) {
    throw new Error(
      "useRecordContext must be used inside <RecordContext>. Wrap the component tree where you need the record."
    );
  }
  return ctx;
}
function useRecordContextOptional() {
  return useContext(RecordContextContext);
}
var EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
function LookupRecordContext({
  columnName,
  onBeforeSave,
  children,
  isVisible = true
}) {
  const parent = useRecordContext();
  if (!isVisible) return null;
  const raw = parent.record?.properties?.[columnName];
  if (!raw?.value || raw.value === EMPTY_GUID || !raw.tableName) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    RecordContext,
    {
      table: raw.tableName,
      id: raw.value,
      ...onBeforeSave && { onBeforeSave },
      children
    }
  );
}

// src/use-column-metadata.ts
function useColumnMetadata(columnName) {
  const { tableMetadata } = useRecordContext();
  return tableMetadata?.columns.find((c) => c.columnName === columnName);
}
function useColumnValue(columnName) {
  const { record } = useRecordContext();
  const metadata = useColumnMetadata(columnName);
  return useMemo(
    () => ({
      raw: readColumnRawValue(record, columnName, metadata),
      formatted: record ? resolveColumnDisplayValue(record, columnName, metadata) : null,
      metadata,
      present: hasColumn(record, columnName, metadata),
      readable: canReadColumn(record, columnName)
    }),
    [record, columnName, metadata]
  );
}
var KEEP_OPEN = /* @__PURE__ */ Symbol.for("@powerportalspro/react/KEEP_OPEN");
var DialogHandleContext = createContext(null);
DialogHandleContext.displayName = "DialogHandle";
function DialogHandleProvider({
  handle,
  children
}) {
  return /* @__PURE__ */ jsx(DialogHandleContext.Provider, { value: handle, children });
}
function useDialogHandle() {
  const handle = useContext(DialogHandleContext);
  if (!handle) {
    throw new Error(
      "useDialogHandle must be used inside content shown with DialogService.showDialogAsync."
    );
  }
  return handle;
}
var DialogServiceContext = createContext(null);
DialogServiceContext.displayName = "DialogService";
var DialogOpenContext = createContext(false);
DialogOpenContext.displayName = "DialogOpen";
function DialogOpenProvider({ open, children }) {
  return /* @__PURE__ */ jsx(DialogOpenContext.Provider, { value: open, children });
}
function useIsDialogOpen() {
  return useContext(DialogOpenContext);
}
function useDialogService() {
  const ctx = useContext(DialogServiceContext);
  if (!ctx) {
    throw new Error(
      "useDialogService must be used inside a <DialogServiceProvider>. Wrap your app in <FluentDialogProvider> from @powerportalspro/react-fluent (or your own implementation) so framework dialogs have somewhere to render."
    );
  }
  return ctx;
}
function DialogServiceProvider({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(DialogServiceContext.Provider, { value, children });
}
async function warnAboutUnsavedChangesAsync(dialog, localizer) {
  const result = await dialog.showConfirmationAsync(localizer.t("app.messages.unsaved-changes"), {
    title: localizer.t("app.messages.unsaved-changes-title"),
    primaryText: localizer.t("app.buttons.yes.label"),
    secondaryText: localizer.t("app.buttons.no.label")
  });
  return !result.canceled;
}
function useWarnAboutUnsavedChanges() {
  const dialog = useDialogService();
  const localizer = useLocalizer();
  return useCallback(
    () => warnAboutUnsavedChangesAsync(dialog, localizer),
    [dialog, localizer]
  );
}

// src/unsaved-changes-warning.ts
function useUnsavedChangesWarning() {
  const ctx = useMainContext();
  const isDirty = ctx?.isDirty ?? false;
  useBeforeUnloadWhen(isDirty);
}
function useUnsavedChangesGuard() {
  const isAnyDirty = useIsAnyDirty();
  const warn = useWarnAboutUnsavedChanges();
  return { isAnyDirty, warn };
}

// src/index.ts
var VERSION = "0.1.0";

export { ASP_NET_CULTURE_COOKIE, AuthStatus, AuthorizedView, BUILT_IN_SYSTEM_ADMIN_ROLE, DEFAULT_URL_LOCALE_PATTERN, DefaultLocalizerProvider, DialogHandleProvider, DialogOpenProvider, DialogServiceProvider, InMemoryOrganizationSettingsCache, InMemoryTableMetadataCache, InMemoryViewMetadataCache, InMemoryViewsForTableCache, KEEP_OPEN, LOCALIZATION_KEY_MODE_QUERY_PARAM, LocaleProvider, LocalizerProvider, LookupRecordContext, MainContext, MainContextProvider, MaskMode, OverlayServiceProvider, PowerPortalsProContext, PowerPortalsProProvider, QueryStatus, RecordContext, SystemAdminView, UnsavedChangesRegistryProvider, VERSION, ValidationProvider, buildLocalePath, createLocalizer, detectLocale, flattenStrings, forgetOperationAccess, getAspNetCultureCookie, getLocaleFromUrlPath, hasPortalRole, hasRole, hasSecurityRole, hasWebRole, highlightMatches, interpolate, isKeyModeEnabled, isSystemAdministrator, reformat, replaceQueryParam, resolveLocalizationUrl, runWithOverlayAsync, setAspNetCultureCookie, shouldPrefixLocale, stripLocalePrefix, useAuth, useAuthOptions, useColumnMetadata, useColumnValidationErrors, useColumnValue, useDataverseUnavailable, useDefaultLocale, useDialogHandle, useDialogService, useFetchRecords, useIsAnyDirty, useIsDialogOpen, useIsSystemAdmin, useIsSystemAdminStatus, useLocale, useLocalization, useLocalizationLoaded, useLocalizationPrefetcher, useLocalizer, useMainContext, useMaskedTextField, useOperationAccess, useOrganizationSettings, useOverlayForTarget, useOverlayService, useOverlayServiceState, usePowerPortalsPro, usePrefixedT, useQueryData, useQueryParam, useRecord, useRecordContext, useRecordContextOptional, useRecordPage, useRecordPages, useRunWithOverlay, useSupportedLocales, useT, useTableMetadata, useTablePermissions, useUnsavedChangesGuard, useUnsavedChangesRegistry, useUnsavedChangesWarning, useUrlCultureStrategy, useUserTimeZone, useValidationContext, useViewDataSource, useViewMetadata, useViewsForTable, useWarnAboutUnsavedChanges, warnAboutUnsavedChangesAsync };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map